import { DIG_NODES, FETCH_NODES, FORAGE_NODES, BEG_NODES, type GatherNodeType } from "./gather.js";
import {
  Tile,
  bakeMapCollision,
  type AmbientCritterDef,
  type AmbientLeashWalkerDef,
  type BegPose,
  type CourtMarkDef,
  type GroundMarkerDef,
  type MapData,
  type PathRibbonDef,
  type ResourceNodeDef,
  type SceneryDef,
  type TeleporterDef,
} from "./map.js";
import { hashNpcId } from "./npcSocial.js";
import { isWalkablePos, nearestWalkablePos } from "./pathfinding.js";
import type { ZoneId } from "./zones.js";

function makeGrid(w: number, h: number, fill: Tile): Tile[][] {
  return Array.from({ length: h }, () => Array<Tile>(w).fill(fill));
}

function inBounds(w: number, h: number, x: number, y: number): boolean {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  return ix >= 0 && iy >= 0 && ix < w && iy < h;
}

function setTile(tiles: Tile[][], w: number, h: number, x: number, y: number, tile: Tile): void {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (ix >= 0 && iy >= 0 && ix < w && iy < h && tiles[iy]) {
    tiles[iy]![ix] = tile;
  }
}

function getTile(tiles: Tile[][], w: number, h: number, x: number, y: number): Tile | undefined {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (ix < 0 || iy < 0 || ix >= w || iy >= h) {
    return undefined;
  }
  return tiles[iy]?.[ix];
}

function hash01(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function fillRect(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  tile: Tile,
): void {
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      setTile(tiles, w, h, x, y, tile);
    }
  }
}

/** Rectangle with nibbled / bulged edges so paths don't look laser-cut. */
function fillSoftRect(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  tile: Tile,
  ragged = 1,
): void {
  for (let y = y0 - ragged; y <= y1 + ragged; y++) {
    for (let x = x0 - ragged; x <= x1 + ragged; x++) {
      const dx = x < x0 ? x0 - x : x > x1 ? x - x1 : 0;
      const dy = y < y0 ? y0 - y : y > y1 ? y - y1 : 0;
      const outside = Math.max(dx, dy);
      if (outside === 0) {
        const edge = Math.min(x - x0, x1 - x, y - y0, y1 - y) === 0;
        if (edge && hash01(x + 3, y + 7) < 0.18) {
          continue;
        }
        setTile(tiles, w, h, x, y, tile);
        continue;
      }
      if (outside > ragged) {
        continue;
      }
      if (hash01(x * 2, y * 3) < 0.42 / outside) {
        setTile(tiles, w, h, x, y, tile);
      }
    }
  }
}

/** Soft ellipse / blob - plazas, meadows, clearings. */
function fillBlob(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  tile: Tile,
): void {
  const rMax = Math.ceil(Math.max(rx, ry) + 2);
  for (let y = cy - rMax; y <= cy + rMax; y++) {
    for (let x = cx - rMax; x <= cx + rMax; x++) {
      const nx = (x - cx) / Math.max(0.5, rx);
      const ny = (y - cy) / Math.max(0.5, ry);
      const d = nx * nx + ny * ny;
      const wobble = (hash01(x, y) - 0.5) * 0.45;
      if (d > 1 + wobble) {
        continue;
      }
      const cur = getTile(tiles, w, h, x, y);
      // Never stamp lawn décor over sports asphalt / plaza / water.
      if (
        cur === Tile.CourtBasket ||
        cur === Tile.CourtTennis ||
        cur === Tile.CourtLine ||
        cur === Tile.Path ||
        cur === Tile.Floor ||
        cur === Tile.Water ||
        cur === Tile.Pier
      ) {
        continue;
      }
      setTile(tiles, w, h, x, y, tile);
    }
  }
}

/** Default ribbon width for park sidewalks (~two dogs abreast). */
const SIDEWALK_WIDTH = 2.55;

/**
 * Soft park sidewalk along a cubic curve (gentle park bend, not a grid).
 * Paints Path tiles for walkability and returns a smooth centerline for ribbon meshes.
 */
function sidewalkCurve(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  /** Perpendicular bulge at midpoints as a fraction of path length (0 = straight). */
  bulge = 0.22,
  width = SIDEWALK_WIDTH,
): PathRibbonDef {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  if (len < 0.35) {
    return sidewalkPolyline(tiles, w, h, [{ x: x0, y: y0 }], width);
  }
  const px = -dy / len;
  const py = dx / len;
  const side = hash01(Math.round(x0 * 3 + x1), Math.round(y0 * 5 + y1)) > 0.5 ? 1 : -1;
  const amp = len * Math.max(0.12, bulge) * side;
  // Asymmetric control points + a soft S-wobble so walks feel poured, not ruler-straight.
  const wobble = (hash01(Math.round(x0 + y1), Math.round(y0 + x1)) - 0.5) * len * 0.08;
  const c1x = x0 + dx * 0.28 + px * (amp + wobble);
  const c1y = y0 + dy * 0.28 + py * (amp + wobble);
  const c2x = x0 + dx * 0.72 + px * (amp * 0.75 - wobble * 0.6);
  const c2y = y0 + dy * 0.72 + py * (amp * 0.75 - wobble * 0.6);
  const steps = Math.max(16, Math.ceil(len * 3.2));
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    const x = u * u * u * x0 + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * x1;
    const y = u * u * u * y0 + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * y1;
    points.push({ x, y });
  }
  return sidewalkPolyline(tiles, w, h, points, width);
}

/** Closed oval promenade around a hub (plaza walk). */
function sidewalkRing(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  radiusX: number,
  radiusY: number,
  width = SIDEWALK_WIDTH,
): PathRibbonDef {
  const steps = Math.max(48, Math.ceil(((radiusX + radiusY) * Math.PI) / 0.85));
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2 - Math.PI / 2;
    // Organic park oval - multi-harmonic wobble so it never reads as a perfect ellipse.
    const wobble =
      1 +
      Math.sin(a * 2 + cx * 0.03) * 0.045 +
      Math.sin(a * 5 + cy * 0.02) * 0.028 +
      Math.cos(a * 3 - cx * 0.01) * 0.018;
    points.push({
      x: cx + Math.cos(a) * radiusX * wobble,
      y: cy + Math.sin(a) * radiusY * wobble,
    });
  }
  return sidewalkPolyline(tiles, w, h, points, width, true);
}

/** Chaikin corner-cutting so ribbon meshes stay curvy (not polyline-faceted). */
function chaikinSmooth(
  points: { x: number; y: number }[],
  passes = 2,
  closed = false,
): { x: number; y: number }[] {
  if (points.length < 3) {
    return points;
  }
  let pts = points;
  for (let pass = 0; pass < passes; pass++) {
    const next: { x: number; y: number }[] = [];
    const n = pts.length;
    const last = n - 1;
    const end = closed ? n : last;
    if (!closed) {
      next.push(pts[0]!);
    }
    for (let i = 0; i < end; i++) {
      const a = pts[i]!;
      const b = pts[closed ? (i + 1) % n : i + 1]!;
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    if (!closed) {
      next.push(pts[last]!);
    } else if (next.length > 0) {
      next.push({ ...next[0]! });
    }
    pts = next;
  }
  return pts;
}

/** Even spacing along a polyline for stable ribbon normals. */
function resamplePolyline(
  points: { x: number; y: number }[],
  spacing = 0.45,
): { x: number; y: number }[] {
  if (points.length < 2) {
    return points;
  }
  const out: { x: number; y: number }[] = [{ ...points[0]! }];
  let dist = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);
    if (segLen < 1e-6) {
      continue;
    }
    let pos = spacing - dist;
    while (pos <= segLen) {
      const t = pos / segLen;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      pos += spacing;
    }
    dist = segLen - (pos - spacing);
    if (dist < 0) {
      dist = 0;
    }
  }
  const last = points[points.length - 1]!;
  const tip = out[out.length - 1]!;
  if (Math.hypot(last.x - tip.x, last.y - tip.y) > 0.08) {
    out.push({ ...last });
  }
  return out;
}

/** Stamp Path disks along a centerline and return the ribbon def. */
function sidewalkPolyline(
  tiles: Tile[][],
  w: number,
  h: number,
  points: { x: number; y: number }[],
  width = SIDEWALK_WIDTH,
  closed = false,
): PathRibbonDef {
  const radius = Math.max(0.55, width * 0.48);
  if (points.length === 0) {
    return { points: [], width };
  }
  // Densify for walkability brush, then smooth/resample for the visual ribbon.
  const dense: { x: number; y: number }[] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    if (i === 0) {
      dense.push(p);
      continue;
    }
    const prev = points[i - 1]!;
    const gap = Math.hypot(p.x - prev.x, p.y - prev.y);
    const mid = Math.max(1, Math.ceil(gap / 0.5));
    for (let s = 1; s <= mid; s++) {
      const t = s / mid;
      dense.push({ x: prev.x + (p.x - prev.x) * t, y: prev.y + (p.y - prev.y) * t });
    }
  }
  for (const p of dense) {
    paintPathBrush(tiles, w, h, p.x, p.y, radius);
  }
  let forSmooth = dense;
  if (closed && dense.length > 3) {
    const a = dense[0]!;
    const b = dense[dense.length - 1]!;
    if (Math.hypot(a.x - b.x, a.y - b.y) < 0.08) {
      forSmooth = dense.slice(0, -1);
    }
  }
  const ribbon = resamplePolyline(chaikinSmooth(forSmooth, 2, closed), 0.4);
  if (closed && ribbon.length > 2) {
    const head = ribbon[0]!;
    const tip = ribbon[ribbon.length - 1]!;
    if (Math.hypot(head.x - tip.x, head.y - tip.y) > 0.05) {
      ribbon.push({ ...head });
    }
  }
  return { points: ribbon, width };
}

/** @deprecated Prefer sidewalkCurve - kept as a thin wrapper for call sites. */
function sidewalkSegment(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  width = SIDEWALK_WIDTH,
): PathRibbonDef {
  return sidewalkCurve(tiles, w, h, x0, y0, x1, y1, 0.22, width);
}

/** Disk brush for walkable Path tiles under the visual ribbon. */
function paintPathBrush(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
): void {
  const r2 = radius * radius;
  const xMin = Math.floor(cx - radius);
  const xMax = Math.ceil(cx + radius);
  const yMin = Math.floor(cy - radius);
  const yMax = Math.ceil(cy + radius);
  for (let y = yMin; y <= yMax; y++) {
    for (let x = xMin; x <= xMax; x++) {
      const ox = x + 0.5 - cx;
      const oy = y + 0.5 - cy;
      if (ox * ox + oy * oy > r2 + 0.2) {
        continue;
      }
      const cur = getTile(tiles, w, h, x, y);
      // Don't carve sidewalk asphalt through sports courts.
      if (
        cur === Tile.CourtBasket ||
        cur === Tile.CourtTennis ||
        cur === Tile.CourtLine ||
        cur === Tile.Water ||
        cur === Tile.Pier
      ) {
        continue;
      }
      setTile(tiles, w, h, x, y, Tile.Path);
    }
  }
}

/** Drop / split ribbon centerlines that drift onto water, piers, or sports courts. */
function clipRibbonOffBlocked(
  tiles: Tile[][],
  w: number,
  h: number,
  ribbon: PathRibbonDef,
): PathRibbonDef[] {
  const out: PathRibbonDef[] = [];
  let current: { x: number; y: number }[] = [];
  // Margin so the ribbon width (~half of path) never overlaps asphalt / water.
  const margin = Math.max(1.15, ribbon.width * 0.55);
  const blockedNear = (px: number, py: number): boolean => {
    const x0 = Math.floor(px - margin);
    const x1 = Math.ceil(px + margin);
    const y0 = Math.floor(py - margin);
    const y1 = Math.ceil(py + margin);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const tile = getTile(tiles, w, h, x, y);
        if (
          tile === Tile.Water ||
          tile === Tile.Pier ||
          tile === Tile.CourtBasket ||
          tile === Tile.CourtTennis ||
          tile === Tile.CourtLine
        ) {
          const dx = x + 0.5 - px;
          const dy = y + 0.5 - py;
          if (dx * dx + dy * dy <= margin * margin) {
            return true;
          }
        }
      }
    }
    return false;
  };
  const flush = () => {
    if (current.length >= 2) {
      out.push({ points: current, width: ribbon.width });
    }
    current = [];
  };
  for (const p of ribbon.points) {
    if (blockedNear(p.x, p.y)) {
      flush();
      continue;
    }
    current.push(p);
  }
  flush();
  return out;
}

function clipAllRibbonsOffBlocked(
  tiles: Tile[][],
  w: number,
  h: number,
  ribbons: PathRibbonDef[],
): PathRibbonDef[] {
  const out: PathRibbonDef[] = [];
  for (const ribbon of ribbons) {
    out.push(...clipRibbonOffBlocked(tiles, w, h, ribbon));
  }
  return out;
}

/** @deprecated Use clipRibbonOffBlocked - kept for call-site clarity during rename. */
function clipRibbonOffWater(
  tiles: Tile[][],
  w: number,
  h: number,
  ribbon: PathRibbonDef,
): PathRibbonDef[] {
  return clipRibbonOffBlocked(tiles, w, h, ribbon);
}

function clipAllRibbonsOffWater(
  tiles: Tile[][],
  w: number,
  h: number,
  ribbons: PathRibbonDef[],
): PathRibbonDef[] {
  return clipAllRibbonsOffBlocked(tiles, w, h, ribbons);
}

function treeBorder(tiles: Tile[][], w: number, h: number, depth = 2): void {
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const edge = Math.min(x, y, w - 1 - x, h - 1 - y);
      const local = depth + (hash01(x, y) > 0.55 ? 1 : 0) - (hash01(x + 9, y + 4) > 0.82 ? 1 : 0);
      if (edge < local) {
        setTile(tiles, w, h, x, y, hash01(x * 3, y * 5) < 0.22 ? Tile.Bush : Tile.Tree);
      } else if (edge === local && hash01(x + 1, y + 2) < 0.35) {
        decorate(tiles, w, h, x, y, hash01(x, y + 8) < 0.5 ? Tile.Bush : Tile.Tree);
      }
    }
  }
}

function isOpen(tile: Tile | undefined): boolean {
  return tile === Tile.Grass || tile === Tile.GrassAlt || tile === Tile.Flowers || tile === Tile.Sand;
}

function decorate(
  tiles: Tile[][],
  w: number,
  h: number,
  x: number,
  y: number,
  tile: Tile,
): void {
  if (isOpen(getTile(tiles, w, h, x, y))) {
    setTile(tiles, w, h, x, y, tile);
  }
}

function sprinkleAlt(tiles: Tile[][], w: number, h: number): void {
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (tiles[y]![x] !== Tile.Grass) {
        continue;
      }
      // Soft meadow mottling - large organic patches, never a checker.
      const n = hash01(x, y) * 0.4 + hash01(Math.floor(x / 5), Math.floor(y / 5)) * 0.6;
      if (n > 0.72) {
        tiles[y]![x] = Tile.GrassAlt;
      }
    }
  }
}

function grove(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
): void {
  for (let y = cy - radius - 1; y <= cy + radius + 1; y++) {
    for (let x = cx - radius - 1; x <= cx + radius + 1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      const lim = radius + (hash01(x, y) - 0.5) * 1.4;
      if (d > lim) {
        continue;
      }
      if (hash01(x * 13, y * 17) < 0.28) {
        decorate(tiles, w, h, x, y, Tile.Bush);
      } else if (hash01(x * 7, y * 3) < 0.72) {
        decorate(tiles, w, h, x, y, Tile.Tree);
      }
    }
  }
}

function rocks(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
): void {
  for (let y = cy - radius - 1; y <= cy + radius + 1; y++) {
    for (let x = cx - radius - 1; x <= cx + radius + 1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > radius + (hash01(x, y) - 0.5) * 1.1) {
        continue;
      }
      if (hash01(x + 2, y + 5) < 0.22) {
        decorate(tiles, w, h, x, y, Tile.Bush);
      } else {
        decorate(tiles, w, h, x, y, Tile.Rock);
      }
    }
  }
}

function pond(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  const y0 = Math.floor(cy - ry - 2);
  const y1 = Math.ceil(cy + ry + 2);
  const x0 = Math.floor(cx - rx - 2);
  const x1 = Math.ceil(cx + rx + 2);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const nx = (x - cx) / (rx + 1.2);
      const ny = (y - cy) / (ry + 1.2);
      // Tiny wobble only - heavy noise left grass islands in the lake.
      const d = nx * nx + ny * ny + (hash01(x, y) - 0.5) * 0.06;
      if (d <= 1) {
        // Shore clears trees/bush so a large lake can carve the perimeter belt.
        setTile(tiles, w, h, x, y, Tile.Sand);
      }
    }
  }
  const wy0 = Math.floor(cy - ry - 1);
  const wy1 = Math.ceil(cy + ry + 1);
  const wx0 = Math.floor(cx - rx - 1);
  const wx1 = Math.ceil(cx + rx + 1);
  for (let y = wy0; y <= wy1; y++) {
    for (let x = wx0; x <= wx1; x++) {
      const nx = (x - cx) / rx;
      const ny = (y - cy) / ry;
      const d = nx * nx + ny * ny + (hash01(x + 1, y) - 0.5) * 0.05;
      if (d <= 1) {
        setTile(tiles, w, h, x, y, Tile.Water);
      }
    }
  }
}

/**
 * After pier decks are painted, restore water under the lake ellipse (keeping
 * Pier tiles) and soft grass banks at the abutments.
 */
function finishLakeBridge(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  const y0 = Math.floor(cy - ry - 3);
  const y1 = Math.ceil(cy + ry + 3);
  const x0 = Math.floor(cx - rx - 3);
  const x1 = Math.ceil(cx + rx + 3);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const cur = getTile(tiles, w, h, x, y);
      if (cur === Tile.Pier) {
        continue;
      }
      const nx = (x - cx) / rx;
      const ny = (y - cy) / ry;
      const d = nx * nx + ny * ny + (hash01(x + 1, y) - 0.5) * 0.05;
      if (d <= 1) {
        // Paths that grazed the lake become water (ribbons are clipped separately).
        setTile(tiles, w, h, x, y, Tile.Water);
        continue;
      }
      const nsx = (x - cx) / (rx + 2.4);
      const nsy = (y - cy) / (ry + 2.0);
      const ds = nsx * nsx + nsy * nsy;
      if (ds <= 1.05) {
        // Grass banks instead of a sand strip along the pier approaches.
        if (cur === Tile.Sand || cur === Tile.Floor || cur === Tile.Flowers) {
          setTile(tiles, w, h, x, y, hash01(x, y) > 0.55 ? Tile.GrassAlt : Tile.Grass);
        }
      }
    }
  }
  // Soft grass shoulders beside pier decks on the shore (not over open water).
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (getTile(tiles, w, h, x, y) !== Tile.Pier) {
        continue;
      }
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
        [2, 0],
        [-2, 0],
        [0, 2],
        [0, -2],
      ] as const) {
        const tx = x + dx;
        const ty = y + dy;
        const shore = getTile(tiles, w, h, tx, ty);
        if (shore !== Tile.Sand && shore !== Tile.Floor) {
          continue;
        }
        // Only convert shore fill - leave deep water alone.
        const nx = (tx - cx) / rx;
        const ny = (ty - cy) / ry;
        if (nx * nx + ny * ny <= 1) {
          continue;
        }
        setTile(tiles, w, h, tx, ty, Tile.Grass);
      }
    }
  }
  eraseLakeIslands(tiles, w, h, cx, cy, rx, ry);
}

/**
 * Remove stray land tiles (grass / path / flowers) sitting in or jutting into the lake.
 * Pier decks are kept. Runs a few erode passes so thin peninsula tips wash away.
 */
function eraseLakeIslands(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  const y0 = Math.floor(cy - ry - 4);
  const y1 = Math.ceil(cy + ry + 4);
  const x0 = Math.floor(cx - rx - 4);
  const x1 = Math.ceil(cx + rx + 4);
  const isLandDebris = (t: Tile | undefined): boolean =>
    t === Tile.Grass ||
    t === Tile.GrassAlt ||
    t === Tile.Flowers ||
    t === Tile.Sand ||
    t === Tile.Path ||
    t === Tile.Bush ||
    t === Tile.Tree;

  for (let pass = 0; pass < 3; pass++) {
    const doomed: { x: number; y: number }[] = [];
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const cur = getTile(tiles, w, h, x, y);
        if (!isLandDebris(cur)) {
          continue;
        }
        const nx = (x - cx) / rx;
        const ny = (y - cy) / ry;
        const d = nx * nx + ny * ny;
        // Anything still inside the lake ellipse is open water.
        if (d <= 1.01) {
          doomed.push({ x, y });
          continue;
        }
        // Only nibble tips that poke into the lake (not the dry promenade).
        if (d > 1.12) {
          continue;
        }
        let ortho = 0;
        let diag = 0;
        for (const [dx, dy, isDiag] of [
          [1, 0, false],
          [-1, 0, false],
          [0, 1, false],
          [0, -1, false],
          [1, 1, true],
          [1, -1, true],
          [-1, 1, true],
          [-1, -1, true],
        ] as const) {
          if (getTile(tiles, w, h, x + dx, y + dy) === Tile.Water) {
            if (isDiag) {
              diag++;
            } else {
              ortho++;
            }
          }
        }
        // Island or thin spit: mostly water on all sides.
        if (ortho >= 3 || (ortho >= 2 && ortho + diag >= 6)) {
          doomed.push({ x, y });
        }
      }
    }
    for (const cell of doomed) {
      setTile(tiles, w, h, cell.x, cell.y, Tile.Water);
    }
  }
}

/**
 * Shorter rail when an end would hang past the pier into open water.
 * trimNeg / trimPos are in local-X after rotY (see lakeBridgeRails).
 */
function railEndFit(trimNeg: boolean, trimPos: boolean): {
  scaleX?: number;
  localOx?: number;
} {
  if (trimNeg && trimPos) {
    return { scaleX: 0.5 };
  }
  if (trimNeg) {
    return { scaleX: 0.55, localOx: 0.22 };
  }
  if (trimPos) {
    return { scaleX: 0.55, localOx: -0.22 };
  }
  return {};
}

/** Scenery rail posts along pier cells that touch Water. */
function lakeBridgeRails(
  tiles: Tile[][],
  w: number,
  h: number,
  scenery: SceneryDef[],
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  let n = 0;
  const y0 = Math.floor(cy - ry - 1);
  const y1 = Math.ceil(cy + ry + 1);
  const x0 = Math.floor(cx - rx - 1);
  const x1 = Math.ceil(cx + rx + 1);
  const isPier = (tx: number, ty: number) => getTile(tiles, w, h, tx, ty) === Tile.Pier;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (!isPier(x, y)) {
        continue;
      }
      const waterN = getTile(tiles, w, h, x, y - 1) === Tile.Water;
      const waterS = getTile(tiles, w, h, x, y + 1) === Tile.Water;
      const waterW = getTile(tiles, w, h, x - 1, y) === Tile.Water;
      const waterE = getTile(tiles, w, h, x + 1, y) === Tile.Water;
      if (!waterN && !waterS && !waterW && !waterE) {
        continue;
      }
      // Continuous edge rails on every water-facing pier cell (corners get two).
      // Place on the pier slab (not the water tile) and aim local +Z at the water.
      // Ends that lack a pier neighbor are scaled shorter so they don't overhang.
      if (waterN) {
        // rotY=π: local +X → world −X
        scenery.push({
          id: `bridge-rail-n-${n++}`,
          kind: "bridgeRail",
          x,
          y,
          rotY: Math.PI,
          ...railEndFit(!isPier(x + 1, y), !isPier(x - 1, y)),
        });
      }
      if (waterS) {
        // rotY=0: local +X → world +X
        scenery.push({
          id: `bridge-rail-s-${n++}`,
          kind: "bridgeRail",
          x,
          y,
          rotY: 0,
          ...railEndFit(!isPier(x - 1, y), !isPier(x + 1, y)),
        });
      }
      if (waterW) {
        // rotY=−π/2: local +X → world +Z (tile +Y)
        scenery.push({
          id: `bridge-rail-w-${n++}`,
          kind: "bridgeRail",
          x,
          y,
          rotY: -Math.PI / 2,
          ...railEndFit(!isPier(x, y - 1), !isPier(x, y + 1)),
        });
      }
      if (waterE) {
        // rotY=π/2: local +X → world −Z (tile −Y)
        scenery.push({
          id: `bridge-rail-e-${n++}`,
          kind: "bridgeRail",
          x,
          y,
          rotY: Math.PI / 2,
          ...railEndFit(!isPier(x, y + 1), !isPier(x, y - 1)),
        });
      }
    }
  }
}

/** Ambient anglers on pier edges, facing open water. */
function lakePierFishermen(
  scenery: SceneryDef[],
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  const half = 1;
  const eLen = Math.round(rx * 0.4);
  const nLen = Math.round(ry * 0.36);
  const wLen = Math.round(rx * 0.34);
  // Facing uses atan2(dz, dx) toward water (map +Y → world +Z).
  const faceS = Math.atan2(1, 0);
  const faceN = Math.atan2(-1, 0);
  const faceE = Math.atan2(0, 1);
  const faceW = Math.atan2(0, -1);
  scenery.push(
    // East fishing dock - long edges + tip platform
    {
      id: "fish-e-s",
      kind: "fishingSpot",
      x: cx + rx - Math.round(eLen * 0.4),
      y: cy + half,
      rotY: faceS,
    },
    {
      id: "fish-e-n",
      kind: "fishingSpot",
      x: cx + rx - 2,
      y: cy - half,
      rotY: faceN,
    },
    {
      id: "fish-e-tip",
      kind: "fishingSpot",
      x: cx + rx + 1,
      y: cy,
      rotY: faceE,
    },
    // North stub west rail
    {
      id: "fish-n-w",
      kind: "fishingSpot",
      x: cx - half,
      y: cy - ry + Math.round(nLen * 0.55),
      rotY: faceW,
    },
    // West stub south rail
    {
      id: "fish-w-s",
      kind: "fishingSpot",
      x: cx - rx + Math.round(wLen * 0.45),
      y: cy + half,
      rotY: faceS,
    },
  );
}

/** Solid axis-aligned wooden deck (no diagonal sampling gaps). */
function fillPier(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  const xa = Math.min(x0, x1);
  const xb = Math.max(x0, x1);
  const ya = Math.min(y0, y1);
  const yb = Math.max(y0, y1);
  for (let y = ya; y <= yb; y++) {
    for (let x = xa; x <= xb; x++) {
      setTile(tiles, w, h, x, y, Tile.Pier);
    }
  }
}

/**
 * Four clean cardinal piers - solid wood stubs from shore into the lake.
 * No diagonals, no mid-lake gaps that look like broken boardwalks.
 */
function paintPondPiers(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  const half = 1; // 3-wide deck
  const nLen = Math.round(ry * 0.36);
  const sLen = Math.round(ry * 0.32);
  const wLen = Math.round(rx * 0.34);
  const eLen = Math.round(rx * 0.4);

  // North stub + shore apron
  fillPier(tiles, w, h, cx - half, cy - ry - 2, cx + half, cy - ry + nLen);
  fillPier(tiles, w, h, cx - half - 1, cy - ry - 2, cx + half + 1, cy - ry);
  // South stub + shore apron
  fillPier(tiles, w, h, cx - half, cy + ry - sLen, cx + half, cy + ry + 2);
  fillPier(tiles, w, h, cx - half - 1, cy + ry, cx + half + 1, cy + ry + 2);
  // West stub + shore apron
  fillPier(tiles, w, h, cx - rx - 2, cy - half, cx - rx + wLen, cy + half);
  fillPier(tiles, w, h, cx - rx - 2, cy - half - 1, cx - rx, cy + half + 1);
  // East stub (slightly longer fishing dock) + shore apron + end platform
  fillPier(tiles, w, h, cx + rx - eLen, cy - half, cx + rx + 2, cy + half);
  fillPier(tiles, w, h, cx + rx, cy - half - 1, cx + rx + 2, cy + half + 1);
  fillPier(tiles, w, h, cx + rx - eLen, cy - half - 1, cx + rx - eLen + 2, cy + half + 1);
}

function paddock(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  gateX: number,
  gateY: number,
): void {
  for (let x = x0; x <= x1; x++) {
    const yo = hash01(x, y0) > 0.7 ? 1 : 0;
    decorate(tiles, w, h, x, y0 + (x % 5 === 0 ? yo : 0), Tile.Fence);
    decorate(tiles, w, h, x, y1 - (x % 7 === 0 ? yo : 0), Tile.Fence);
  }
  for (let y = y0; y <= y1; y++) {
    const xo = hash01(x0, y) > 0.7 ? 1 : 0;
    decorate(tiles, w, h, x0 + (y % 5 === 0 ? xo : 0), y, Tile.Fence);
    decorate(tiles, w, h, x1 - (y % 6 === 0 ? xo : 0), y, Tile.Fence);
  }
  setTile(tiles, w, h, gateX, gateY, Tile.Grass);
  if (inBounds(w, h, gateX + 1, gateY)) {
    setTile(tiles, w, h, gateX + 1, gateY, Tile.Grass);
  }
}

/** Soft shrub rim so districts read as separate rooms (skips Path / Floor / Water / courts). */
function hedgeBorder(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  density = 0.72,
): void {
  const place = (x: number, y: number) => {
    const cur = getTile(tiles, w, h, x, y);
    if (
      cur === Tile.Path ||
      cur === Tile.Floor ||
      cur === Tile.Water ||
      cur === Tile.Sand ||
      cur === Tile.Fence ||
      cur === Tile.CourtBasket ||
      cur === Tile.CourtTennis ||
      cur === Tile.CourtLine
    ) {
      return;
    }
    if (hash01(x + 9, y + 3) < density) {
      decorate(tiles, w, h, x, y, hash01(x, y) > 0.55 ? Tile.Bush : Tile.Flowers);
    }
  };
  for (let x = x0; x <= x1; x++) {
    place(x, y0);
    place(x, y1);
  }
  for (let y = y0; y <= y1; y++) {
    place(x0, y);
    place(x1, y);
  }
}

/** Paint a sports field as darker grass with soft oval edges. */
function athleticField(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  const cx = (x0 + x1) * 0.5;
  const cy = (y0 + y1) * 0.5;
  const rx = Math.max(2, (x1 - x0) * 0.55 + 1.5);
  const ry = Math.max(2, (y1 - y0) * 0.55 + 1.2);
  softLawnBlob(tiles, w, h, cx, cy, rx, ry, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, cx - rx * 0.35, cy + ry * 0.2, rx * 0.45, ry * 0.55, Tile.GrassAlt);
}

function hMark(marks: CourtMarkDef[], x0: number, x1: number, y: number): void {
  marks.push({ x0, y0: y, x1, y1: y });
}

function vMark(marks: CourtMarkDef[], x: number, y0: number, y1: number): void {
  marks.push({ x0: x, y0, x1: x, y1 });
}

function rectMarks(marks: CourtMarkDef[], x0: number, y0: number, x1: number, y1: number): void {
  hMark(marks, x0, x1, y0);
  hMark(marks, x0, x1, y1);
  vMark(marks, x0, y0, y1);
  vMark(marks, x1, y0, y1);
}

/** Polyline arc in tile space (angles in radians, 0 = +X). */
function arcMarks(
  marks: CourtMarkDef[],
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  steps = 12,
): void {
  let px = cx + Math.cos(a0) * r;
  let py = cy + Math.sin(a0) * r;
  for (let i = 1; i <= steps; i++) {
    const t = a0 + ((a1 - a0) * i) / steps;
    const x = cx + Math.cos(t) * r;
    const y = cy + Math.sin(t) * r;
    marks.push({ x0: px, y0: py, x1: x, y1: y });
    px = x;
    py = y;
  }
}

/**
 * N-S basketball pad: dark asphalt + boundary, midcourt, keys, free-throw arcs,
 * center circle, lane hashes, and three-point arcs. Hoops sit near N/S baselines.
 */
function paintBasketballCourt(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  marks: CourtMarkDef[],
): void {
  // Inclusive tile span → world edges at x0..(x1+1), y0..(y1+1).
  const x0 = cx - 6;
  const x1 = cx + 5;
  const y0 = cy - 10;
  const y1 = cy + 10;
  const left = x0;
  const right = x1 + 1;
  const top = y0;
  const bottom = y1 + 1;
  const midX = (left + right) * 0.5;
  const midY = (top + bottom) * 0.5;

  fillRect(tiles, w, h, x0, y0, x1, y1, Tile.CourtBasket);

  // Outer boundary
  rectMarks(marks, left, top, right, bottom);
  // Midcourt line
  hMark(marks, left, right, midY);
  // Center circle
  arcMarks(marks, midX, midY, 2.2, 0, Math.PI * 2, 24);

  // Keys / paint under each hoop + free-throw line + FT semicircle toward center.
  const keyHalf = 2.4;
  const keyDepth = 4.4;
  // North key (opens south toward midcourt)
  rectMarks(marks, midX - keyHalf, top, midX + keyHalf, top + keyDepth);
  hMark(marks, midX - keyHalf, midX + keyHalf, top + keyDepth);
  arcMarks(marks, midX, top + keyDepth, 2.15, 0.05, Math.PI - 0.05, 14);
  // South key (opens north toward midcourt)
  rectMarks(marks, midX - keyHalf, bottom - keyDepth, midX + keyHalf, bottom);
  hMark(marks, midX - keyHalf, midX + keyHalf, bottom - keyDepth);
  arcMarks(marks, midX, bottom - keyDepth, 2.15, Math.PI + 0.05, Math.PI * 2 - 0.05, 14);

  // Lane hash ticks on the outside of each key
  for (const y of [top + 1.5, top + 2.6, top + 3.6, bottom - 1.5, bottom - 2.6, bottom - 3.6]) {
    hMark(marks, midX - keyHalf - 0.65, midX - keyHalf, y);
    hMark(marks, midX + keyHalf, midX + keyHalf + 0.65, y);
  }

  // Three-point arcs from each baseline (park-scale, readable)
  const threeR = 5.6;
  arcMarks(marks, midX, top + 1.0, threeR, 0.18, Math.PI - 0.18, 18);
  arcMarks(marks, midX, bottom - 1.0, threeR, Math.PI + 0.18, Math.PI * 2 - 0.18, 18);
  // Straight corners of the 3pt line to the sidelines
  const threeCornerX = midX + Math.cos(0.18) * threeR;
  hMark(marks, left, midX - (threeCornerX - midX), top + 1.0);
  hMark(marks, midX + (threeCornerX - midX), right, top + 1.0);
  hMark(marks, left, midX - (threeCornerX - midX), bottom - 1.0);
  hMark(marks, midX + (threeCornerX - midX), right, bottom - 1.0);
}

/**
 * E-W tennis court (net across center X): green hard court + doubles/singles
 * sidelines, baselines, service boxes, and center service line.
 */
function paintTennisCourt(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  marks: CourtMarkDef[],
): void {
  const x0 = cx - 7;
  const x1 = cx + 8;
  const y0 = cy - 4;
  const y1 = cy + 4;
  const left = x0;
  const right = x1 + 1;
  const top = y0;
  const bottom = y1 + 1;
  const netX = (left + right) * 0.5;
  const midY = (top + bottom) * 0.5;
  // Doubles alleys: singles inset ~1 tile from doubles sidelines.
  const singlesTop = top + 1;
  const singlesBot = bottom - 1;
  // Service line sits ~21/39 of the way from net to baseline on a real court.
  const serviceWest = netX - (netX - left) * (21 / 39);
  const serviceEast = netX + (right - netX) * (21 / 39);

  fillRect(tiles, w, h, x0, y0, x1, y1, Tile.CourtTennis);

  // Doubles outer boundary (baselines + doubles sidelines)
  rectMarks(marks, left, top, right, bottom);
  // Singles sidelines (full length)
  hMark(marks, left, right, singlesTop);
  hMark(marks, left, right, singlesBot);
  // Net line (visual cue under the 3D net)
  vMark(marks, netX, top, bottom);
  // Service lines (between singles sidelines only)
  vMark(marks, serviceWest, singlesTop, singlesBot);
  vMark(marks, serviceEast, singlesTop, singlesBot);
  // Center service line (net → each service line)
  hMark(marks, serviceWest, netX, midY);
  hMark(marks, netX, serviceEast, midY);
  // Center marks on each baseline
  const tick = 0.45;
  vMark(marks, left, midY - tick, midY + tick);
  vMark(marks, right, midY - tick, midY + tick);
}

/** Flower ring around a feature (only replaces grass). */
function flowerRing(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rxInner: number,
  ryInner: number,
  rxOuter: number,
  ryOuter: number,
): void {
  const rMax = Math.ceil(Math.max(rxOuter, ryOuter) + 2);
  for (let y = cy - rMax; y <= cy + rMax; y++) {
    for (let x = cx - rMax; x <= cx + rMax; x++) {
      const nx = (x - cx) / Math.max(0.5, rxOuter);
      const ny = (y - cy) / Math.max(0.5, ryOuter);
      const dout = nx * nx + ny * ny;
      const nix = (x - cx) / Math.max(0.5, rxInner);
      const niy = (y - cy) / Math.max(0.5, ryInner);
      const din = nix * nix + niy * niy;
      if (dout <= 1.05 && din >= 0.92) {
        const cur = getTile(tiles, w, h, x, y);
        if (cur === Tile.Grass || cur === Tile.GrassAlt) {
          setTile(tiles, w, h, x, y, Tile.Flowers);
        }
      }
    }
  }
}

/** Semicircle seating tier (audience faces +Y / stage). */
function amphSeatArc(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  stageY: number,
  radius: number,
  tile: Tile,
  thickness = 2,
): void {
  for (let a = Math.PI * 0.18; a <= Math.PI * 0.82; a += 0.07) {
    for (let t = 0; t < thickness; t++) {
      const r = radius + t;
      const x = Math.round(cx + Math.cos(a) * r);
      const y = Math.round(stageY - Math.sin(a) * r);
      const cur = getTile(tiles, w, h, x, y);
      if (
        cur === Tile.Grass ||
        cur === Tile.GrassAlt ||
        cur === Tile.Flowers ||
        cur === Tile.Tree ||
        cur === Tile.Bush ||
        cur === Tile.Floor ||
        cur === Tile.Sand
      ) {
        setTile(tiles, w, h, x, y, tile);
      }
    }
  }
}

/** One dedicated splash playground in the open east meadow (clear of amph / sports). */
function splashPlaygroundHub(CX: number, CY: number): { x: number; y: number } {
  // East of plaza, north of the amph bowl - not SE where the stage seating lives.
  return { x: CX + 46, y: CY - 6 };
}

/** Concrete splash courts packed with irrigation pipes - single playground section. */
function splashPadSites(
  hub: { x: number; y: number },
): { x: number; y: number; count: number; spread: number; rx: number; ry: number }[] {
  return [
    { x: hub.x, y: hub.y, count: 16, spread: 7.2, rx: 9.0, ry: 8.0 },
    { x: hub.x + 9, y: hub.y - 5, count: 12, spread: 5.6, rx: 6.8, ry: 5.8 },
    { x: hub.x - 8, y: hub.y + 7, count: 12, spread: 5.6, rx: 6.8, ry: 5.8 },
  ];
}

/** Tall green irrigation pipes packed on concrete splash pads. */
function lawnSprayerScenery(
  pads: { x: number; y: number; count: number; spread: number }[],
): SceneryDef[] {
  const spots: { x: number; y: number }[] = [];
  for (const pad of pads) {
    for (let i = 0; i < pad.count; i++) {
      const a = i * 2.399963 + pad.x * 0.01;
      const r = Math.sqrt((i + 0.35) / pad.count) * pad.spread;
      const x = Math.round(pad.x + Math.cos(a) * r);
      const y = Math.round(pad.y + Math.sin(a) * r);
      if (spots.some((s) => Math.hypot(s.x - x, s.y - y) < 1.65)) {
        continue;
      }
      spots.push({ x, y });
    }
  }

  return spots.map((spot, i) => ({
    id: `spray-${i + 1}`,
    kind: "lawnSprayer" as const,
    x: spot.x,
    y: spot.y,
  }));
}

/**
 * Outdoor amphitheater: grand stage (south), seating bowl rising to the north.
 */
function buildAmphitheater(tiles: Tile[][], w: number, h: number, cx = 100, cy = 88): void {
  // Grand bowl - clears overlapping trees / hedges far out.
  fillBlob(tiles, w, h, cx, cy, 40, 34, Tile.GrassAlt);
  fillBlob(tiles, w, h, cx, cy, 34, 28, Tile.Grass);
  fillBlob(tiles, w, h, cx, cy + 2, 26, 20, Tile.GrassAlt);

  // Stage pad (south) - slightly compact so the bowl reads around it.
  fillSoftRect(tiles, w, h, cx - 18, cy + 12, cx + 18, cy + 21, Tile.Floor, 0);
  fillSoftRect(tiles, w, h, cx - 14, cy + 10, cx + 14, cy + 12, Tile.Floor, 0);
  fillSoftRect(tiles, w, h, cx - 10, cy + 8, cx + 10, cy + 10, Tile.Floor, 0);

  // Orchestra lawn / flat in front of the stage.
  fillSoftRect(tiles, w, h, cx - 14, cy + 2, cx + 14, cy + 8, Tile.Sand, 1);

  // Shakespeare-in-the-park lawn bowl (grass, not paved bleachers) - picnics sit here.
  amphSeatArc(tiles, w, h, cx, cy + 10, 11, Tile.Grass, 3);
  amphSeatArc(tiles, w, h, cx, cy + 10, 15, Tile.GrassAlt, 3);
  amphSeatArc(tiles, w, h, cx, cy + 10, 19, Tile.Grass, 3);
  amphSeatArc(tiles, w, h, cx, cy + 10, 23, Tile.GrassAlt, 3);
  amphSeatArc(tiles, w, h, cx, cy + 10, 27, Tile.Grass, 2);

  // Wide center aisle from the north rim down toward the stage (plaza pavers, not sidewalk Path).
  fillRect(tiles, w, h, cx - 3, cy - 22, cx + 2, cy + 8, Tile.Floor);
  fillRect(tiles, w, h, cx - 4, cy - 6, cx + 3, cy - 2, Tile.Floor);

  // Planting rim - light flower accents well outside the seating bowl (not a dense hedge).
  flowerRing(tiles, w, h, cx, cy, 40, 34, 43, 37);
}

/**
 * Tight Shakespeare-in-the-park picnic clusters on the amph lawn, facing the stage.
 * Blankets sit close together so the audience reads as one cozy crowd.
 */
function amphitheaterPicnicLayout(amph: { x: number; y: number }): {
  x: number;
  y: number;
  rotY: number;
  kind: "family" | "date" | "friends";
}[] {
  const focusX = amph.x;
  const focusY = amph.y + 10;
  const stageX = amph.x;
  const stageY = amph.y + 16;
  // Sparse rings — a light Shakespeare lawn crowd, not a packed festival.
  const tiers: { r: number; count: number }[] = [
    { r: 12.0, count: 3 },
    { r: 15.5, count: 4 },
    { r: 19.0, count: 3 },
  ];
  // Prefer couples / friend groups; only a couple of big family blankets.
  const kinds: ("family" | "date" | "friends")[] = [
    "date",
    "friends",
    "date",
    "family",
    "friends",
    "date",
    "friends",
    "family",
    "date",
    "friends",
  ];
  const spots: { x: number; y: number; rotY: number; kind: "family" | "date" | "friends" }[] = [];
  let n = 0;
  for (const tier of tiers) {
    const a0 = Math.PI * 0.2;
    const a1 = Math.PI * 0.8;
    for (let i = 0; i < tier.count; i++) {
      const t = (i + 0.5) / tier.count;
      const a = a0 + (a1 - a0) * t;
      const x = focusX + Math.cos(a) * tier.r;
      const y = focusY - Math.sin(a) * tier.r;
      // Keep the center aisle clear for the approach.
      if (Math.abs(x - focusX) < 2.5) {
        continue;
      }
      const rotY = Math.atan2(stageX - x, stageY - y);
      spots.push({
        x,
        y,
        rotY,
        kind: kinds[n % kinds.length]!,
      });
      n += 1;
    }
  }
  return spots;
}

/** Picnic blankets on the amph lawn (client spawns ParkPicnic groups). */
function amphitheaterPicnicScenery(amph: { x: number; y: number }): SceneryDef[] {
  return amphitheaterPicnicLayout(amph).map((spot, i) => ({
    id: `amp-picnic-${spot.kind}-${i + 1}`,
    kind: "picnic" as const,
    // Keep fractional coords so dense clusters don't snap onto the same tile.
    x: Math.round(spot.x * 10) / 10,
    y: Math.round(spot.y * 10) / 10,
    rotY: spot.rotY,
  }));
}

/** Side-line audience pups only - humans come from amph picnic blankets. */
function amphitheaterCrowd(amph: { x: number; y: number }): ResourceNodeDef[] {
  const pups: { id: string; name: string; ox: number; oy: number; line: string }[] = [
    { id: "pup-amp-bravo", name: "Bravo", ox: -22, oy: -8, line: "Standing ovation practice. From the lawn. Mostly." },
    { id: "pup-amp-cue", name: "Cue", ox: 22, oy: -10, line: "Waiting for my cue. Or a treat. Either works." },
    { id: "pup-amp-gallery", name: "Gallery", ox: -24, oy: -18, line: "Blanket seats, rich vibes." },
    { id: "pup-amp-hush", name: "Hush", ox: 24, oy: -16, line: "Shhh. Also, is that cheese?" },
  ];
  return pups.map((pup) => npc(pup.id, pup.name, amph.x + pup.ox, amph.y + pup.oy, "guide", pup.line));
}

/** True if tile is sports asphalt (basketball / tennis / painted lines). */
function isSportsCourtTile(tile: Tile | undefined): boolean {
  return tile === Tile.CourtBasket || tile === Tile.CourtTennis || tile === Tile.CourtLine;
}

/**
 * Clear trees / bushes / rocks / flower beds in a margin around a court rect
 * so canopies don't hang over the play surface.
 */
function clearCourtApron(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  margin = 3,
): void {
  for (let y = y0 - margin; y <= y1 + margin; y++) {
    for (let x = x0 - margin; x <= x1 + margin; x++) {
      const cur = getTile(tiles, w, h, x, y);
      if (isSportsCourtTile(cur)) {
        continue;
      }
      if (cur === Tile.Tree || cur === Tile.Bush || cur === Tile.Rock || cur === Tile.Flowers) {
        setTile(tiles, w, h, x, y, hash01(x, y) > 0.55 ? Tile.GrassAlt : Tile.Grass);
      }
    }
  }
}

/** Keep dig / fetch / forage piles off court asphalt (and out of the apron). */
function ensureGatherOffCourts(
  tiles: Tile[][],
  w: number,
  h: number,
  nodes: ResourceNodeDef[],
): void {
  for (const node of nodes) {
    if (node.kind !== "digSpot" && node.kind !== "fetchSpot" && node.kind !== "forageSpot") {
      continue;
    }
    const tile = getTile(tiles, w, h, Math.round(node.x), Math.round(node.y));
    if (!isSportsCourtTile(tile) && !isHazardForPeople(tile)) {
      // Still push off anything sitting in a court apron if neighbors are asphalt.
      let besideCourt = false;
      for (const [dx, dy] of [
        [0, 0],
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        if (isSportsCourtTile(getTile(tiles, w, h, Math.round(node.x) + dx, Math.round(node.y) + dy))) {
          besideCourt = true;
          break;
        }
      }
      if (!besideCourt) {
        continue;
      }
    }
    const safe = nearestPeopleTile(tiles, w, h, node.x, node.y, 36);
    node.x = safe.x;
    node.y = safe.y;
  }
}

/** Soft blob that only paints over open lawn (never Path / Floor / Water). */
function softLawnBlob(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  tile: Tile,
  /** When painting Flowers, keep only this fraction of cells (sparse meadow). */
  density = 1,
): void {
  const rMax = Math.ceil(Math.max(rx, ry) + 2);
  for (let y = cy - rMax; y <= cy + rMax; y++) {
    for (let x = cx - rMax; x <= cx + rMax; x++) {
      const nx = (x - cx) / Math.max(0.5, rx);
      const ny = (y - cy) / Math.max(0.5, ry);
      const d = nx * nx + ny * ny;
      const wobble = (hash01(x, y) - 0.5) * 0.45;
      if (d > 1 + wobble) {
        continue;
      }
      const cur = getTile(tiles, w, h, x, y);
      if (cur === Tile.Grass || cur === Tile.GrassAlt || cur === Tile.Flowers || cur === Tile.Sand) {
        if (tile === Tile.Flowers && density < 1 && hash01(x * 3, y * 5) > density) {
          continue;
        }
        setTile(tiles, w, h, x, y, tile);
      }
    }
  }
}

/**
 * Strip most flower beds near busy hubs (plaza, amph, courts, playgrounds).
 * Quiet meadows keep denser blooms for dogs to zoom through.
 */
function clearFlowersNearBusy(
  tiles: Tile[][],
  w: number,
  h: number,
  hubs: { x: number; y: number; r: number; keep?: number }[],
): void {
  for (const hub of hubs) {
    const keep = hub.keep ?? 0.12;
    const rMax = Math.ceil(hub.r + 1);
    for (let y = Math.round(hub.y) - rMax; y <= Math.round(hub.y) + rMax; y++) {
      for (let x = Math.round(hub.x) - rMax; x <= Math.round(hub.x) + rMax; x++) {
        const dx = x - hub.x;
        const dy = y - hub.y;
        if (dx * dx + dy * dy > hub.r * hub.r) {
          continue;
        }
        if (getTile(tiles, w, h, x, y) !== Tile.Flowers) {
          continue;
        }
        if (hash01(x + 11, y + 19) < keep) {
          continue;
        }
        setTile(tiles, w, h, x, y, hash01(x, y) > 0.55 ? Tile.GrassAlt : Tile.Grass);
      }
    }
  }
}

/** True if people / picnic props should not stand here. */
function isHazardForPeople(tile: Tile | undefined): boolean {
  return (
    tile === undefined ||
    tile === Tile.Water ||
    tile === Tile.Tree ||
    tile === Tile.Bush ||
    tile === Tile.Rock ||
    tile === Tile.Fence ||
    // Keep sitters / crumbs / benches off the play surface.
    tile === Tile.CourtBasket ||
    tile === Tile.CourtTennis ||
    tile === Tile.CourtLine
  );
}

/**
 * Cut trees / bushes / rocks / flowers into lawn so people aren't stuck in forest
 * or standing in a flower bed.
 * Never paints over Water, Pier, Path, Floor, or court asphalt.
 */
function clearPeoplePad(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  const rMax = Math.ceil(Math.max(rx, ry) + 1);
  for (let y = Math.round(cy) - rMax; y <= Math.round(cy) + rMax; y++) {
    for (let x = Math.round(cx) - rMax; x <= Math.round(cx) + rMax; x++) {
      const nx = (x - cx) / Math.max(0.5, rx);
      const ny = (y - cy) / Math.max(0.5, ry);
      if (nx * nx + ny * ny > 1.05) {
        continue;
      }
      const cur = getTile(tiles, w, h, x, y);
      if (cur === Tile.Tree || cur === Tile.Bush || cur === Tile.Rock || cur === Tile.Flowers) {
        setTile(tiles, w, h, x, y, hash01(x, y) > 0.55 ? Tile.GrassAlt : Tile.Grass);
      }
    }
  }
}

/** Spiral search for the nearest tile people can stand on (not water / forest / rock). */
function nearestPeopleTile(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  maxR = 28,
): { x: number; y: number } {
  const sx = Math.round(x0);
  const sy = Math.round(y0);
  if (!isHazardForPeople(getTile(tiles, w, h, sx, sy))) {
    return { x: sx, y: sy };
  }
  for (let r = 1; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
          continue;
        }
        const x = sx + dx;
        const y = sy + dy;
        if (!isHazardForPeople(getTile(tiles, w, h, x, y))) {
          return { x, y };
        }
      }
    }
  }
  return { x: sx, y: sy };
}

/** Prefer a pad where the whole ellipse is free of water/forest (for picnics / frisbee). */
function nearestPeoplePad(
  tiles: Tile[][],
  w: number,
  h: number,
  x0: number,
  y0: number,
  rx: number,
  ry: number,
  maxR = 36,
): { x: number; y: number } {
  const sx = Math.round(x0);
  const sy = Math.round(y0);
  const padClear = (cx: number, cy: number): boolean => {
    const rMax = Math.ceil(Math.max(rx, ry));
    for (let y = cy - rMax; y <= cy + rMax; y++) {
      for (let x = cx - rMax; x <= cx + rMax; x++) {
        const nx = (x - cx) / Math.max(0.5, rx);
        const ny = (y - cy) / Math.max(0.5, ry);
        if (nx * nx + ny * ny > 1.02) {
          continue;
        }
        const cur = getTile(tiles, w, h, x, y);
        // Water / pier / courts / OOB block the pad; trees are cut after relocate.
        if (
          cur === undefined ||
          cur === Tile.Water ||
          cur === Tile.Pier ||
          cur === Tile.CourtBasket ||
          cur === Tile.CourtTennis ||
          cur === Tile.CourtLine
        ) {
          return false;
        }
      }
    }
    return true;
  };
  if (padClear(sx, sy)) {
    return { x: sx, y: sy };
  }
  for (let r = 1; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
          continue;
        }
        const x = sx + dx;
        const y = sy + dy;
        if (padClear(x, y)) {
          return { x, y };
        }
      }
    }
  }
  return nearestPeopleTile(tiles, w, h, x0, y0, maxR);
}

type PeopleSpot = { x: number; y: number };

/**
 * Nudge people / picnic / vendor spots off water & forest, then clear vegetation pads.
 * Mutates node / scenery coordinates in place.
 */
function ensurePeopleClearings(
  tiles: Tile[][],
  w: number,
  h: number,
  nodes: ResourceNodeDef[],
  scenery: SceneryDef[],
): void {
  const pads: { spot: PeopleSpot; rx: number; ry: number }[] = [];

  for (const node of nodes) {
    const human =
      node.kind === "begSpot" ||
      (node.kind === "npc" && !node.id.startsWith("pup-"));
    if (!human) {
      continue;
    }
    const safe = nearestPeopleTile(tiles, w, h, node.x, node.y);
    node.x = safe.x;
    node.y = safe.y;
    // Wide enough that flower clusters don't poke through sitters / vendors.
    pads.push({ spot: node, rx: 3.2, ry: 3.2 });
  }

  for (const item of scenery) {
    let rx = 0;
    let ry = 0;
    let needPad = false;
    if (item.kind === "picnic") {
      // Amph picnic blankets sit close - use a tighter clear pad than lawn picnics.
      const amph = item.id.startsWith("amp-picnic");
      rx = amph ? 2.6 : 5.5;
      ry = amph ? 2.2 : 4.5;
      needPad = true;
    } else if (item.kind === "frisbeeGame") {
      // Wide open lawn - kids stand ~7 tiles apart; keep trees well back.
      rx = 9;
      ry = 8;
      needPad = true;
    } else if (item.kind === "drumCircle") {
      rx = 4.5;
      ry = 4.5;
      needPad = true;
    } else if (item.kind === "pavilion") {
      rx = 4;
      ry = 4;
      needPad = true;
    } else if (
      item.kind === "bbqGrill" ||
      item.kind === "picnicTable" ||
      item.kind === "hotdogStand" ||
      item.kind === "iceCreamCart" ||
      item.kind === "pretzelCart" ||
      item.kind === "balloonCart" ||
      item.kind === "popcornCart"
    ) {
      rx = 2.5;
      ry = 2.5;
    } else if (item.kind === "stadiumSeat") {
      // Keep arc placement on the seating bowl; only clear plants under seats.
      pads.push({ spot: item, rx: 1.8, ry: 1.5 });
      continue;
    } else if (item.kind === "bench") {
      rx = 2.2;
      ry = 2.2;
    } else {
      continue;
    }
    const safe = needPad
      ? nearestPeoplePad(tiles, w, h, item.x, item.y, rx, ry)
      : nearestPeopleTile(tiles, w, h, item.x, item.y);
    item.x = safe.x;
    item.y = safe.y;
    pads.push({ spot: item, rx, ry });
  }

  for (const pad of pads) {
    clearPeoplePad(tiles, w, h, pad.spot.x, pad.spot.y, pad.rx, pad.ry);
  }
}

/** Scenery that must stay put (court gear / amph seats) - never nudge for portals. */
const PORTAL_FIXED_SCENERY = new Set([
  "stadiumSeat",
  "stage",
  "basketHoop",
  "tennisNet",
  "bridgeRail",
  "fishingSpot",
]);

type PortalRect = { x0: number; y0: number; x1: number; y1: number };

function portalRects(
  teleporters: TeleporterDef[],
  markers: GroundMarkerDef[],
  margin = 2,
): PortalRect[] {
  const rects: PortalRect[] = [];
  for (const pad of teleporters) {
    rects.push({
      x0: pad.x - margin,
      y0: pad.y - margin,
      x1: pad.x + pad.w + margin - 1,
      y1: pad.y + pad.h + margin - 1,
    });
  }
  for (const mark of markers) {
    if (mark.kind !== "spawn") {
      continue;
    }
    rects.push({
      x0: mark.x - margin,
      y0: mark.y - margin,
      x1: mark.x + mark.w + margin - 1,
      y1: mark.y + mark.h + margin - 1,
    });
  }
  return rects;
}

function inPortalRect(x: number, y: number, rects: PortalRect[]): boolean {
  const ix = Math.round(x);
  const iy = Math.round(y);
  for (const r of rects) {
    if (ix >= r.x0 && ix <= r.x1 && iy >= r.y0 && iy <= r.y1) {
      return true;
    }
  }
  return false;
}

/**
 * Push benches / carts / people / gather piles off travel portals + spawn pads,
 * and strip vegetation from those footprints.
 */
function ensureClearOfPortals(
  tiles: Tile[][],
  w: number,
  h: number,
  nodes: ResourceNodeDef[],
  scenery: SceneryDef[],
  teleporters: TeleporterDef[],
  markers: GroundMarkerDef[],
): void {
  const rects = portalRects(teleporters, markers, 2);

  const nudgeOff = (spot: PeopleSpot): void => {
    if (!inPortalRect(spot.x, spot.y, rects)) {
      return;
    }
    const sx = Math.round(spot.x);
    const sy = Math.round(spot.y);
    for (let r = 1; r <= 28; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
            continue;
          }
          const x = sx + dx;
          const y = sy + dy;
          if (!inBounds(w, h, x, y)) {
            continue;
          }
          if (inPortalRect(x, y, rects)) {
            continue;
          }
          if (isHazardForPeople(getTile(tiles, w, h, x, y))) {
            continue;
          }
          spot.x = x;
          spot.y = y;
          return;
        }
      }
    }
  };

  for (const item of scenery) {
    if (PORTAL_FIXED_SCENERY.has(item.kind) || item.id.startsWith("amp-")) {
      continue;
    }
    nudgeOff(item);
  }
  for (const node of nodes) {
    nudgeOff(node);
  }

  // Open lawn under every portal / spawn so trees don't regrow into the pad.
  for (const r of rects) {
    const cx = (r.x0 + r.x1) * 0.5;
    const cy = (r.y0 + r.y1) * 0.5;
    const rx = (r.x1 - r.x0) * 0.5 + 1.5;
    const ry = (r.y1 - r.y0) * 0.5 + 1.5;
    clearPeoplePad(tiles, w, h, cx, cy, rx, ry);
  }
}

/**
 * Frisbee kids need open sky: push games off pavilion roofs and cut a wide
 * tree-free meadow around the final toss midpoints.
 */
function keepFrisbeeInOpenMeadow(
  tiles: Tile[][],
  w: number,
  h: number,
  scenery: SceneryDef[],
): void {
  const pavilions = scenery.filter((item) => item.kind === "pavilion");
  const minDist = 15;
  for (const item of scenery) {
    if (item.kind !== "frisbeeGame") {
      continue;
    }
    for (const pav of pavilions) {
      const dx = item.x - pav.x;
      const dy = item.y - pav.y;
      const dist = Math.hypot(dx, dy);
      if (dist >= minDist) {
        continue;
      }
      const ang = dist > 0.1 ? Math.atan2(dy, dx) : Math.PI / 4;
      item.x = Math.round(pav.x + Math.cos(ang) * minDist);
      item.y = Math.round(pav.y + Math.sin(ang) * minDist);
      const safe = nearestPeoplePad(tiles, w, h, item.x, item.y, 9, 8);
      item.x = safe.x;
      item.y = safe.y;
    }
    clearPeoplePad(tiles, w, h, item.x, item.y, 10, 8);
    softLawnBlob(tiles, w, h, item.x, item.y, 9, 7, Tile.Grass);
    clearPeoplePad(tiles, w, h, item.x, item.y, 10, 8);
  }
}

/**
 * Soft content for the padded rim around the old park footprint -
 * picnic meadows, flower walks, and groves so the perimeter isn't empty.
 */
function populateParkMargins(
  tiles: Tile[][],
  w: number,
  h: number,
  ox: number,
  oy: number,
  iw: number,
  ih: number,
): void {
  const innerB = oy + ih - 1;

  // North meadow strip (between perimeter walk and inner park)
  athleticField(tiles, w, h, 16, 10, w - 18, oy - 2);
  // Soft meadows on the rim - keep some dense flower walks away from the inner hubs.
  softLawnBlob(tiles, w, h, Math.floor(w * 0.28), 14, 8, 5, Tile.Flowers, 0.7);
  softLawnBlob(tiles, w, h, Math.floor(w * 0.48), 13, 7, 4.5, Tile.Flowers, 0.65);
  softLawnBlob(tiles, w, h, Math.floor(w * 0.68), 15, 8, 5, Tile.Sand);
  grove(tiles, w, h, Math.floor(w * 0.2), 13, 3);
  grove(tiles, w, h, Math.floor(w * 0.82), 12, 3);
  rocks(tiles, w, h, Math.floor(w * 0.55), 16, 2);

  // South grove strip
  for (let y = innerB + 2; y < h - 6; y++) {
    for (let x = 14; x < w - 14; x++) {
      if (hash01(x, y) > 0.58) {
        decorate(tiles, w, h, x, y, Tile.Tree);
      } else if (hash01(x + 2, y) > 0.74) {
        decorate(tiles, w, h, x, y, Tile.Bush);
      }
    }
  }
  softLawnBlob(tiles, w, h, Math.floor(w * 0.42), h - 14, 7, 5, Tile.Flowers, 0.65);
  softLawnBlob(tiles, w, h, Math.floor(w * 0.62), h - 15, 7, 5, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, Math.floor(w * 0.78), h - 13, 5, 4, Tile.Sand);
  pond(tiles, w, h, Math.floor(w * 0.35), h - 12, 3.5, 2.2);

  // West picnic band
  softLawnBlob(tiles, w, h, 14, Math.floor(h * 0.32), 8, 9, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, 13, Math.floor(h * 0.48), 6, 7, Tile.Flowers, 0.6);
  softLawnBlob(tiles, w, h, 15, Math.floor(h * 0.66), 6, 5, Tile.Sand);
  grove(tiles, w, h, 12, Math.floor(h * 0.38), 3);
  grove(tiles, w, h, 11, Math.floor(h * 0.72), 2);
  rocks(tiles, w, h, 16, Math.floor(h * 0.58), 2);

  // East garden band
  softLawnBlob(tiles, w, h, w - 14, Math.floor(h * 0.36), 6.5, 7, Tile.Flowers, 0.6);
  softLawnBlob(tiles, w, h, w - 13, Math.floor(h * 0.54), 6, 6, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, w - 15, Math.floor(h * 0.7), 6, 5, Tile.GrassAlt);
  grove(tiles, w, h, w - 12, Math.floor(h * 0.44), 2);
  grove(tiles, w, h, w - 11, Math.floor(h * 0.68), 2);
  flowerRing(tiles, w, h, w - 14, Math.floor(h * 0.5), 5, 5, 8, 7);

  // Corner picnic clearings
  softLawnBlob(tiles, w, h, 18, 18, 6, 5, Tile.Sand);
  softLawnBlob(tiles, w, h, w - 18, 18, 6, 5, Tile.Sand);
  softLawnBlob(tiles, w, h, 18, h - 18, 5, 4, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, w - 18, h - 18, 5, 4, Tile.Flowers, 0.55);
  grove(tiles, w, h, 22, 20, 2);
  grove(tiles, w, h, w - 22, 20, 2);
  grove(tiles, w, h, 20, h - 20, 2);
  grove(tiles, w, h, w - 20, h - 20, 2);
}

function gatherNode(
  id: string,
  type: GatherNodeType,
  x: number,
  y: number,
  opts?: { name?: string; begPose?: BegPose },
): ResourceNodeDef {
  const kind =
    type.skillId === "digging" ? "digSpot"
    : type.skillId === "fetching" ? "fetchSpot"
    : type.skillId === "begging" ? "begSpot"
    : "forageSpot";
  return {
    id,
    kind,
    name: opts?.name ?? type.name,
    skillId: type.skillId,
    x,
    y,
    itemId: type.itemId,
    reqLevel: type.reqLevel,
    xp: type.xp,
    durationMs: type.durationMs,
    respawnMs: type.respawnMs,
    begPose: opts?.begPose,
  };
}

function npc(
  id: string,
  name: string,
  x: number,
  y: number,
  npcKind: ResourceNodeDef["npcKind"],
  line?: string,
): ResourceNodeDef {
  const node: ResourceNodeDef = { id, kind: "npc", name, x, y, npcKind, line };
  // Guides / shopkeepers (and other non-service flavors) can be begged for snacks.
  if (npcKind === "guide" || npcKind === "shopkeeper") {
    const beg = BEG_NODES[hashNpcId(id) % BEG_NODES.length]!;
    node.itemId = beg.itemId;
    node.skillId = beg.skillId;
    node.reqLevel = beg.reqLevel;
    node.xp = beg.xp;
    node.durationMs = beg.durationMs;
    node.respawnMs = beg.respawnMs;
  }
  return node;
}

function mapOf(
  zoneId: ZoneId,
  name: string,
  w: number,
  h: number,
  tiles: Tile[][],
  nodes: ResourceNodeDef[],
  teleporters: TeleporterDef[],
  markers: GroundMarkerDef[] = [],
  scenery: SceneryDef[] = [],
  critters: AmbientCritterDef[] = [],
  pathRibbons: PathRibbonDef[] = [],
  courtMarks: CourtMarkDef[] = [],
  leashWalkers: AmbientLeashWalkerDef[] = [],
): MapData {
  return bakeMapCollision({
    width: w,
    height: h,
    zoneId,
    name,
    tiles,
    nodes,
    teleporters,
    markers,
    scenery,
    critters,
    pathRibbons,
    courtMarks,
    leashWalkers: leashWalkers.length > 0 ? leashWalkers : undefined,
  });
}

function tuple(p: { x: number; y: number }): [number, number] {
  return [p.x, p.y];
}

/** Annular sidewalk ribbon (circle ring of Path slabs). */
function paintAnnulus(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  rInner: number,
  rOuter: number,
  tile: Tile,
): void {
  const rMax = Math.ceil(rOuter) + 1;
  for (let y = cy - rMax; y <= cy + rMax; y++) {
    for (let x = cx - rMax; x <= cx + rMax; x++) {
      const d = Math.hypot(x - cx, y - cy);
      if (d >= rInner && d <= rOuter) {
        setTile(tiles, w, h, x, y, tile);
      }
    }
  }
}

/** Radial spoke of Path from center outward along an angle. */
function paintRadial(
  tiles: Tile[][],
  w: number,
  h: number,
  cx: number,
  cy: number,
  angle: number,
  r0: number,
  r1: number,
  halfWidth: number,
  tile: Tile,
): void {
  const steps = Math.max(1, Math.ceil((r1 - r0) * 2.2));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const r = r0 + (r1 - r0) * t;
    const px = cx + Math.cos(angle) * r;
    const py = cy + Math.sin(angle) * r;
    const hw = halfWidth;
    for (let oy = -hw; oy <= hw; oy++) {
      for (let ox = -hw; ox <= hw; ox++) {
        if (ox * ox + oy * oy <= hw * hw + 0.6) {
          setTile(tiles, w, h, Math.round(px + ox), Math.round(py + oy), tile);
        }
      }
    }
  }
}

export function createHubMap(): MapData {
  // Big circular Dogdell Park: Community Plaza at the center, four outer districts.
  // Spawn sits due north of the plaza. Fill the ring densely so lawns aren't empty.
  const w = 220;
  const h = 220;
  const CX = 110;
  const CY = 110;
  const R = 102;
  const OUTER_R = 66;
  const plaza = { x: CX, y: CY, angle: 0 };
  // Four outer hubs on the diagonals (north kept clear for spawn approach).
  const outerAt = (angle: number) => ({
    x: Math.round(CX + Math.cos(angle) * OUTER_R),
    y: Math.round(CY + Math.sin(angle) * OUTER_R),
    angle,
  });
  const sports = outerAt(-Math.PI / 4); // NE
  // Amph sits farther out so the grand bowl has room to breathe (stay inside the tree ring).
  const AMPH_OUTER_R = 68;
  const amph = {
    x: Math.round(CX + Math.cos(Math.PI / 4) * AMPH_OUTER_R),
    y: Math.round(CY + Math.sin(Math.PI / 4) * AMPH_OUTER_R),
    angle: Math.PI / 4,
  };
  // Pond sits further SW and is much larger (~8× water area) than the other hubs.
  const POND_OUTER_R = 86;
  const POND_RX = 40;
  const POND_RY = 31;
  const pondAngle = (Math.PI * 3) / 4;
  const pondQ = {
    x: Math.round(CX + Math.cos(pondAngle) * POND_OUTER_R),
    y: Math.round(CY + Math.sin(pondAngle) * POND_OUTER_R),
    angle: pondAngle,
  };
  const play = outerAt((-Math.PI * 3) / 4); // NW
  // Dedicated splash-pipe playground in the open east meadow (away from amph / courts).
  const splashPlay = splashPlaygroundHub(CX, CY);
  // Outer pads sit at district entrances (toward plaza), not deep in each hub.
  const ENTRANCE_IN = 16;
  // Sports pad sits further plaza-ward so it doesn't land between the courts.
  const SPORTS_ENTRANCE_IN = 28;
  // Pond pad sits on the plaza-side shore, not out on the water.
  const POND_ENTRANCE_IN = 42;
  const gateOf = (hub: { x: number; y: number; angle: number }, inset = ENTRANCE_IN) => ({
    x: Math.round(hub.x - Math.cos(hub.angle) * inset),
    y: Math.round(hub.y - Math.sin(hub.angle) * inset),
  });
  const sportsGate = gateOf(sports, SPORTS_ENTRANCE_IN);
  // Amph pad at the plaza-side aisle entrance (north rim of the seating bowl).
  const amphGate = { x: amph.x, y: amph.y - 24 };
  const pondGate = gateOf(pondQ, POND_ENTRANCE_IN);
  const playGate = gateOf(play);
  const spawnY = CY - 14; // due north of plaza pad

  const tiles = makeGrid(w, h, Tile.Grass);
  sprinkleAlt(tiles, w, h);

  // Forest outside the circle; trees mostly hug the perimeter inside.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const d = Math.hypot(x - CX, y - CY);
      if (d > R + 0.35) {
        setTile(tiles, w, h, x, y, hash01(x, y) < 0.18 ? Tile.Bush : Tile.Tree);
      } else if (d > R - 7) {
        // Perimeter belt inside the park.
        if (hash01(x + 2, y + 1) < 0.72) {
          setTile(tiles, w, h, x, y, hash01(x, y) < 0.28 ? Tile.Bush : Tile.Tree);
        }
      }
    }
  }

  // --- Community Plaza (center) ---
  // Compact paver disk under the travel pad; lawn wraps the circle for an idyllic hub.
  fillBlob(tiles, w, h, plaza.x, plaza.y, 10.5, 9.5, Tile.Floor);
  fillBlob(tiles, w, h, plaza.x - 3, plaza.y + 2, 5.5, 5, Tile.Floor);
  fillBlob(tiles, w, h, plaza.x + 3, plaza.y - 2, 5, 4.5, Tile.Floor);
  // Soft meadow collar outside the plaza sidewalk ring (~16.8 radius).
  softLawnBlob(tiles, w, h, plaza.x, plaza.y, 26, 24, Tile.Grass);
  softLawnBlob(tiles, w, h, plaza.x, plaza.y, 23, 21, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, plaza.x - 18, plaza.y + 14, 4.5, 3.5, Tile.Flowers, 0.62);
  softLawnBlob(tiles, w, h, plaza.x + 18, plaza.y - 13, 4.5, 3.5, Tile.Flowers, 0.58);
  softLawnBlob(tiles, w, h, plaza.x + 14, plaza.y + 17, 4, 3.2, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, plaza.x - 15, plaza.y - 16, 4, 3.2, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, plaza.x + 20, plaza.y + 4, 3.5, 2.8, Tile.Flowers, 0.5);
  softLawnBlob(tiles, w, h, plaza.x - 20, plaza.y - 3, 3.5, 2.8, Tile.Flowers, 0.5);
  fillBlob(tiles, w, h, plaza.x, plaza.y + 18, 3.2, 2.2, Tile.Sand);

  // --- Sports (NE) ---
  // Tennis stays near the sports hub; basketball sits farther west with a grass alley between.
  const basket = { x: sports.x - 24, y: sports.y };
  const courtMarks: CourtMarkDef[] = [];
  paintTennisCourt(tiles, w, h, sports.x, sports.y - 5, courtMarks);
  paintTennisCourt(tiles, w, h, sports.x, sports.y + 5, courtMarks);
  paintBasketballCourt(tiles, w, h, basket.x, basket.y, courtMarks);
  // Soft grass aprons so courts don't sit in a hard rectangle of lawn.
  softLawnBlob(tiles, w, h, sports.x, sports.y - 5, 14, 5, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, sports.x, sports.y + 5, 14, 5, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, basket.x, basket.y, 9, 13, Tile.GrassAlt);
  // Grass strip between basketball (east edge ~ basket.x+6) and tennis (west ~ sports.x-7).
  softLawnBlob(tiles, w, h, (basket.x + 6 + sports.x - 7) * 0.5, sports.y, 4, 12, Tile.Grass);
  athleticField(tiles, w, h, sports.x - 6, sports.y - 18, sports.x + 14, sports.y - 13);
  // Quiet corner blooms - kept clear of court aprons / benches.
  softLawnBlob(tiles, w, h, sports.x + 14, sports.y + 16, 3.5, 3, Tile.Flowers, 0.45);
  softLawnBlob(tiles, w, h, basket.x - 16, sports.y + 8, 3, 2.5, Tile.Flowers, 0.4);
  fillBlob(tiles, w, h, sports.x + 8, sports.y - 16, 5, 3, Tile.Sand);

  // --- Amphitheater (SE) ---
  buildAmphitheater(tiles, w, h, amph.x, amph.y);
  // Flower meadows sit past the bowl / snack carts - not in the crowd.
  softLawnBlob(tiles, w, h, amph.x - 36, amph.y + 22, 6, 4.5, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, amph.x + 34, amph.y + 24, 5.5, 4, Tile.Flowers, 0.5);
  fillBlob(tiles, w, h, amph.x + 28, amph.y - 14, 9, 6, Tile.Sand);

  // --- Pond (SW) - large lake (~8× prior water area) ---
  fillBlob(tiles, w, h, pondQ.x, pondQ.y, POND_RX + 10, POND_RY + 8, Tile.Sand);
  pond(tiles, w, h, pondQ.x, pondQ.y, POND_RX, POND_RY);
  flowerRing(
    tiles,
    w,
    h,
    pondQ.x,
    pondQ.y,
    POND_RX + 4,
    POND_RY + 3,
    POND_RX + 12,
    POND_RY + 10,
  );
  fillBlob(tiles, w, h, pondQ.x + 34, pondQ.y + 28, 8, 6, Tile.Flowers);
  fillBlob(tiles, w, h, pondQ.x - 34, pondQ.y + 22, 8, 6, Tile.Flowers);
  fillBlob(tiles, w, h, pondQ.x - 22, pondQ.y - 36, 9, 6, Tile.Sand);
  fillBlob(tiles, w, h, pondQ.x + 30, pondQ.y - 30, 7, 5, Tile.Flowers);
  fillBlob(tiles, w, h, pondQ.x + 8, pondQ.y + 38, 9, 6, Tile.Sand);

  // --- Playground (NW) ---
  // Soft sand yard + rubber/concrete play court dogs can trot across.
  fillBlob(tiles, w, h, play.x, play.y, 18, 16, Tile.Sand);
  fillBlob(tiles, w, h, play.x - 10, play.y + 5, 8, 7, Tile.Sand);
  fillBlob(tiles, w, h, play.x + 9, play.y - 4, 7, 6, Tile.Sand);
  fillBlob(tiles, w, h, play.x + 4, play.y + 10, 6, 4, Tile.Sand);
  fillSoftRect(tiles, w, h, play.x - 7, play.y - 5, play.x + 7, play.y + 5, Tile.Floor, 1);
  fillSoftRect(tiles, w, h, play.x - 3, play.y - 8, play.x + 3, play.y - 5, Tile.Floor, 0);
  fillSoftRect(tiles, w, h, play.x + 5, play.y + 2, play.x + 10, play.y + 7, Tile.Floor, 1);
  rocks(tiles, w, h, play.x - 10, play.y - 7, 3);
  rocks(tiles, w, h, play.x + 10, play.y + 8, 2);
  softLawnBlob(tiles, w, h, play.x, play.y - 16, 3.5, 2.5, Tile.Flowers, 0.4);

  // --- Splash playground (east meadow): concrete water-play courts ---
  softLawnBlob(tiles, w, h, splashPlay.x, splashPlay.y, 16, 14, Tile.GrassAlt);
  softLawnBlob(tiles, w, h, splashPlay.x - 14, splashPlay.y + 12, 4, 3.5, Tile.Flowers, 0.45);
  softLawnBlob(tiles, w, h, splashPlay.x + 16, splashPlay.y - 12, 4, 3.5, Tile.Flowers, 0.45);

  // Open mid-park accents: meadows and lawns (no grove clusters on approaches).
  const fillers: { x: number; y: number; kind: "flowers" | "sand" | "field" | "rocks" }[] = [
    { x: CX, y: CY - 36, kind: "flowers" },
    { x: CX + 28, y: CY - 28, kind: "field" },
    { x: CX - 28, y: CY - 28, kind: "field" },
    { x: CX + 38, y: CY, kind: "flowers" },
    { x: CX - 38, y: CY, kind: "flowers" },
    { x: CX, y: CY + 40, kind: "flowers" },
    { x: CX + 18, y: CY + 48, kind: "sand" },
    { x: CX + 48, y: CY + 16, kind: "flowers" },
    { x: CX + 20, y: CY - 55, kind: "flowers" },
    { x: CX - 20, y: CY - 55, kind: "flowers" },
    { x: CX + 52, y: CY + 40, kind: "field" },
  ];
  for (const f of fillers) {
    if (Math.hypot(f.x - CX, f.y - CY) > R - 10) {
      continue;
    }
    // Keep mid-park accents off the big SW lake.
    if (Math.hypot(f.x - pondQ.x, f.y - pondQ.y) < Math.max(POND_RX, POND_RY) + 14) {
      continue;
    }
    if (f.kind === "flowers") {
      // Quiet mid-park meadows - denser so Zoomies through blooms feels special.
      softLawnBlob(tiles, w, h, f.x, f.y, 6, 4.5, Tile.Flowers, 0.72);
    } else if (f.kind === "sand") {
      softLawnBlob(tiles, w, h, f.x, f.y, 5, 4, Tile.Sand);
    } else if (f.kind === "field") {
      athleticField(tiles, w, h, f.x - 6, f.y - 4, f.x + 6, f.y + 4);
    } else {
      rocks(tiles, w, h, f.x, f.y, 2);
    }
  }

  // Sparse random shade trees inside (skip hubs + approach wedges to teleporters).
  const hubs = [plaza, sports, amph, pondQ, play, splashPlay];
  for (let y = CY - R + 10; y <= CY + R - 10; y++) {
    for (let x = CX - R + 10; x <= CX + R - 10; x++) {
      const d = Math.hypot(x - CX, y - CY);
      if (d < 24 || d > R - 10) {
        continue;
      }
      let nearHub = false;
      for (const hub of hubs) {
        const clearR =
          hub === pondQ
            ? Math.max(POND_RX, POND_RY) + 8
            : hub === amph
              ? 40
              : hub === splashPlay
                ? 18
                : hub === sports
                  ? 30
                  : 16;
        if (Math.hypot(x - hub.x, y - hub.y) < clearR) {
          nearHub = true;
          break;
        }
      }
      if (nearHub) {
        continue;
      }
      const ang = Math.atan2(y - CY, x - CX);
      let onApproach = Math.abs(ang + Math.PI / 2) < 0.3;
      for (const hub of [sports, amph, pondQ, play]) {
        let da = Math.abs(ang - hub.angle);
        if (da > Math.PI) {
          da = Math.PI * 2 - da;
        }
        if (da < 0.3) {
          onApproach = true;
          break;
        }
      }
      if (onApproach) {
        continue;
      }
      if (hash01(x * 3, y * 5) > 0.93) {
        decorate(tiles, w, h, x, y, Tile.Tree);
      } else if (hash01(x + 7, y * 2) > 0.955) {
        decorate(tiles, w, h, x, y, Tile.Bush);
      } else if (hash01(x * 2, y + 9) > 0.945) {
        const cur = getTile(tiles, w, h, x, y);
        if (cur === Tile.Grass || cur === Tile.GrassAlt) {
          setTile(tiles, w, h, x, y, Tile.Flowers);
        }
      } else if (hash01(x, y + 3) > 0.96) {
        const cur = getTile(tiles, w, h, x, y);
        if (cur === Tile.Grass || cur === Tile.GrassAlt) {
          setTile(tiles, w, h, x, y, Tile.Sand);
        }
      }
    }
  }

  softLawnBlob(tiles, w, h, CX + 32, CY - 18, 6, 4.5, Tile.Flowers, 0.65);
  softLawnBlob(tiles, w, h, CX - 34, CY - 16, 5.5, 4.5, Tile.Flowers, 0.6);
  softLawnBlob(tiles, w, h, CX + 24, CY + 34, 6, 5, Tile.Sand);
  softLawnBlob(tiles, w, h, CX - 26, CY + 32, 6, 4.5, Tile.Flowers, 0.65);
  softLawnBlob(tiles, w, h, CX + 48, CY + 8, 4.5, 3.5, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, CX - 50, CY + 6, 5, 4, Tile.Sand);
  softLawnBlob(tiles, w, h, CX + 18, CY - 42, 4.5, 3.5, Tile.Flowers, 0.55);
  softLawnBlob(tiles, w, h, CX - 20, CY + 48, 6, 4, Tile.Sand);
  softLawnBlob(tiles, w, h, CX + 55, CY - 20, 4, 4.5, Tile.Flowers, 0.6);
  softLawnBlob(tiles, w, h, CX - 52, CY - 22, 4.5, 3.5, Tile.Flowers, 0.55);
  // North meadow - drum circle on open grass (where the lawn opens behind the path).
  const drumAt = { x: CX - 17, y: CY - 51 };
  softLawnBlob(tiles, w, h, drumAt.x - 14, drumAt.y + 10, 4.5, 3.5, Tile.Flowers, 0.5);
  softLawnBlob(tiles, w, h, drumAt.x + 14, drumAt.y - 8, 4.5, 3.5, Tile.Flowers, 0.5);

  // Cookout clearings on the far rim - pavilions + frisbee lawns away from the plaza.
  const cookouts: { id: string; x: number; y: number; label: string }[] = [
    { id: "ne", x: CX + 86, y: CY - 36, label: "Sunny Cookout" },
    { id: "nw", x: CX - 88, y: CY - 30, label: "Grove Picnic" },
    { id: "e", x: CX + 90, y: CY + 20, label: "East Pavilion" },
    { id: "w", x: CX - 88, y: CY + 28, label: "West Pavilion" },
    { id: "se", x: CX + 58, y: CY + 72, label: "South BBQ" },
    // South of the pond shore - never inside the water ellipse.
    {
      id: "sw",
      x: Math.round(pondQ.x - 12),
      y: Math.round(pondQ.y + POND_RY + 14),
      label: "Lakeside Grill",
    },
    { id: "n", x: CX - 36, y: CY - 86, label: "North Meadow Grill" },
  ];
  for (const spot of cookouts) {
    // Cut forest first - softLawnBlob alone never replaces trees.
    clearPeoplePad(tiles, w, h, spot.x, spot.y, 12, 11);
    // Open meadow well clear of the pavilion footprint for frisbee kids.
    clearPeoplePad(tiles, w, h, spot.x + 16, spot.y + 14, 11, 9);
    softLawnBlob(tiles, w, h, spot.x, spot.y, 10, 9, Tile.Sand);
    softLawnBlob(tiles, w, h, spot.x - 8, spot.y + 7, 3.5, 3, Tile.Flowers, 0.45);
    softLawnBlob(tiles, w, h, spot.x + 7, spot.y - 4, 5, 4, Tile.GrassAlt);
    softLawnBlob(tiles, w, h, spot.x + 16, spot.y + 14, 10, 8, Tile.Grass);
  }

  // Lean sidewalk network: plaza ring + spokes + one outer loop + shore ring.
  // No court-crossing chords; cookouts share a sparse rim (not a spoke to every grill).
  const PLAZA_RX = 16.8;
  const PLAZA_RY = 15.2;
  const ringAt = (ang: number) => ({
    x: CX + Math.cos(ang) * PLAZA_RX,
    y: CY + Math.sin(ang) * PLAZA_RY,
  });
  const angTo = (p: { x: number; y: number }) => Math.atan2(p.y - CY, p.x - CX);
  const spawnPad = { x: CX, y: spawnY + 1 };
  const sportsPad = { x: sportsGate.x, y: sportsGate.y };
  const amphPad = { x: amphGate.x, y: amphGate.y };
  const pondPad = { x: pondGate.x, y: pondGate.y };
  const playPad = { x: playGate.x, y: playGate.y };
  // Shore anchors sit outside the water ellipse (used by pond district links).
  const shoreN = { x: pondQ.x, y: pondQ.y - POND_RY - 6 };
  const shoreE = { x: pondQ.x + POND_RX + 6, y: pondQ.y };
  const shoreS = { x: pondQ.x, y: pondQ.y + POND_RY + 6 };
  const shoreW = { x: pondQ.x - POND_RX - 6, y: pondQ.y };
  // Bypass waypoints well clear of basketball / tennis asphalt.
  const sportsNorth = { x: Math.round(sports.x - 4), y: Math.round(sports.y - 22) };
  const sportsWest = { x: Math.round(basket.x - 12), y: Math.round(sports.y) };
  const sportsEast = { x: Math.round(sports.x + 16), y: Math.round(sports.y) };

  const pathRibbons: PathRibbonDef[] = [
    sidewalkRing(tiles, w, h, CX, CY, PLAZA_RX, PLAZA_RY, 2.75),
    sidewalkCurve(
      tiles, w, h,
      ringAt(-Math.PI / 2).x, ringAt(-Math.PI / 2).y,
      spawnPad.x, spawnPad.y,
      0.08, 2.6,
    ),
    sidewalkCurve(
      tiles, w, h,
      ringAt(angTo(sportsPad)).x, ringAt(angTo(sportsPad)).y,
      sportsPad.x, sportsPad.y,
      0.1, 2.5,
    ),
    sidewalkCurve(
      tiles, w, h,
      ringAt(angTo(amphPad)).x, ringAt(angTo(amphPad)).y,
      amphPad.x, amphPad.y,
      0.1, 2.5,
    ),
    sidewalkCurve(
      tiles, w, h,
      ringAt(angTo(pondPad)).x, ringAt(angTo(pondPad)).y,
      pondPad.x, pondPad.y,
      0.1, 2.5,
    ),
    sidewalkCurve(
      tiles, w, h,
      ringAt(angTo(playPad)).x, ringAt(angTo(playPad)).y,
      playPad.x, playPad.y,
      0.1, 2.5,
    ),
    // Outer district loop - west/north/east of courts (never across asphalt).
    sidewalkCurve(tiles, w, h, playPad.x, playPad.y, sportsNorth.x, sportsNorth.y, 0.1, 2.25),
    sidewalkCurve(tiles, w, h, sportsNorth.x, sportsNorth.y, sportsWest.x, sportsWest.y, 0.08, 2.15),
    sidewalkCurve(tiles, w, h, sportsWest.x, sportsWest.y, sportsPad.x, sportsPad.y, 0.08, 2.15),
    sidewalkCurve(tiles, w, h, sportsPad.x, sportsPad.y, sportsEast.x, sportsEast.y, 0.08, 2.15),
    sidewalkCurve(tiles, w, h, sportsEast.x, sportsEast.y, amphPad.x, amphPad.y, 0.12, 2.3),
    sidewalkCurve(tiles, w, h, amphPad.x, amphPad.y, shoreE.x, shoreE.y, 0.1, 2.3),
    sidewalkCurve(tiles, w, h, shoreW.x, shoreW.y, playPad.x, playPad.y, 0.1, 2.3),
    // Splash sits on a short triangle with the plaza ring + amph gate.
    sidewalkCurve(
      tiles, w, h,
      ringAt(0).x, ringAt(0).y,
      splashPlay.x, splashPlay.y,
      0.1, 2.3,
    ),
    sidewalkCurve(tiles, w, h, splashPlay.x, splashPlay.y, amphPad.x, amphPad.y, 0.12, 2.2),
    // Amph seating aisle loop.
    sidewalkCurve(tiles, w, h, amphPad.x, amphPad.y, amph.x - 18, amph.y - 4, 0.1, 2.1),
    sidewalkCurve(tiles, w, h, amph.x - 18, amph.y - 4, amph.x + 18, amph.y - 4, 0.08, 2.05),
    sidewalkCurve(tiles, w, h, amph.x + 18, amph.y - 4, amphPad.x, amphPad.y, 0.1, 2.1),
    // Drum meadow loop via spawn (stays north of the courts).
    sidewalkCurve(tiles, w, h, playPad.x, playPad.y, drumAt.x, drumAt.y, 0.1, 2.1),
    sidewalkCurve(tiles, w, h, drumAt.x, drumAt.y, spawnPad.x, spawnPad.y, 0.1, 2.15),
  ];

  // Lakeshore promenade.
  pathRibbons.push(
    sidewalkRing(tiles, w, h, pondQ.x, pondQ.y, POND_RX + 6.5, POND_RY + 5.5, 2.25),
    sidewalkCurve(tiles, w, h, pondPad.x, pondPad.y, shoreN.x, shoreN.y, 0.08, 2.1),
    sidewalkCurve(tiles, w, h, pondPad.x, pondPad.y, shoreW.x, shoreW.y, 0.08, 2.1),
  );

  // Sparse cookout rim + a few hub feeders (not a spoke to every grill).
  const cookoutOrdered = [...cookouts].sort(
    (a, b) => Math.atan2(a.y - CY, a.x - CX) - Math.atan2(b.y - CY, b.x - CX),
  );
  const lakeClear = (ax: number, ay: number, bx: number, by: number): boolean => {
    const midX = (ax + bx) * 0.5;
    const midY = (ay + by) * 0.5;
    const nx = (midX - pondQ.x) / POND_RX;
    const ny = (midY - pondQ.y) / POND_RY;
    return nx * nx + ny * ny >= 1.08;
  };
  for (let i = 0; i < cookoutOrdered.length; i++) {
    const a = cookoutOrdered[i]!;
    const b = cookoutOrdered[(i + 1) % cookoutOrdered.length]!;
    if (!lakeClear(a.x, a.y, b.x, b.y)) {
      const via = [shoreN, shoreE, shoreS, shoreW].reduce((best, s) => {
        const d = Math.hypot(a.x - s.x, a.y - s.y) + Math.hypot(b.x - s.x, b.y - s.y);
        return d < best.d ? { s, d } : best;
      }, { s: shoreS, d: Infinity });
      pathRibbons.push(
        sidewalkCurve(tiles, w, h, a.x, a.y, via.s.x, via.s.y, 0.08, 2.0),
        sidewalkCurve(tiles, w, h, via.s.x, via.s.y, b.x, b.y, 0.08, 2.0),
      );
      continue;
    }
    pathRibbons.push(sidewalkCurve(tiles, w, h, a.x, a.y, b.x, b.y, 0.1, 2.05));
  }
  // Three feeders tie the rim into the core (enough for access, not a web).
  const cookoutFeeders = ["n", "ne", "sw"].map((id) => cookouts.find((c) => c.id === id)!).filter(Boolean);
  const dryHubs = [sportsPad, amphPad, pondPad, playPad, spawnPad, shoreN, shoreE, shoreS, shoreW];
  for (const spot of cookoutFeeders) {
    let nearest: { x: number; y: number } = sportsPad;
    let best = Infinity;
    for (const hub of dryHubs) {
      if (!lakeClear(hub.x, hub.y, spot.x, spot.y)) {
        continue;
      }
      const d = Math.hypot(spot.x - hub.x, spot.y - hub.y);
      if (d < best) {
        best = d;
        nearest = hub;
      }
    }
    pathRibbons.push(
      sidewalkCurve(tiles, w, h, nearest.x, nearest.y, spot.x, spot.y, 0.1, 2.05),
    );
  }

  // Pond piers: solid wood stubs from each shore, then restore water around them.
  paintPondPiers(tiles, w, h, pondQ.x, pondQ.y, POND_RX, POND_RY);
  finishLakeBridge(tiles, w, h, pondQ.x, pondQ.y, POND_RX, POND_RY);

  // Clip ribbons after water restore; courts are clipped again after final asphalt stamp.
  let pathRibbonsDry = clipAllRibbonsOffBlocked(tiles, w, h, pathRibbons);

  // Clear trees/bushes beside paths and around teleporter hubs so approaches stay open.
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const cur = getTile(tiles, w, h, x, y);
      if (cur !== Tile.Tree && cur !== Tile.Bush) {
        continue;
      }
      let clear = false;
      for (const hub of [plaza, sports, amph, pondQ, play, drumAt, ...cookouts]) {
        if (Math.hypot(x - hub.x, y - hub.y) < (hub === amph ? 42 : hub === drumAt ? 18 : 14)) {
          clear = true;
          break;
        }
      }
      if (!clear) {
        for (let oy = -2; oy <= 2 && !clear; oy++) {
          for (let ox = -2; ox <= 2; ox++) {
            if (getTile(tiles, w, h, x + ox, y + oy) === Tile.Path) {
              clear = true;
              break;
            }
          }
        }
      }
      if (clear) {
        setTile(tiles, w, h, x, y, hash01(x, y) > 0.55 ? Tile.GrassAlt : Tile.Grass);
      }
    }
  }

  // Clear floors under teleporters + spawn pad (outer pads at district gates).
  fillBlob(tiles, w, h, plaza.x, plaza.y, 5.2, 5.0, Tile.Floor);
  for (const gate of [sportsGate, amphGate, pondGate, playGate]) {
    fillBlob(tiles, w, h, gate.x, gate.y, 5.0, 4.8, Tile.Floor);
  }
  fillBlob(tiles, w, h, CX, spawnY, 6.5, 6.2, Tile.Floor);

  // Concrete splash pads for the irrigation-pipe playground (SE meadow only).
  const splashPads = splashPadSites(splashPlay);
  for (const pad of splashPads) {
    fillBlob(tiles, w, h, pad.x, pad.y, pad.rx, pad.ry, Tile.Floor);
  }
  // Small plaza apron where the sidewalks meet the splash courts.
  fillBlob(tiles, w, h, splashPlay.x, splashPlay.y, 4.5, 4.2, Tile.Floor);

  const nodes: ResourceNodeDef[] = [
    // Plaza stays lean: greet, trade, Doggy Bank / forge. Snacks live out in the districts.
    npc(
      "npc-greeter",
      "Greeter Gus",
      CX + 3,
      spawnY + 3,
      "guide",
      "Welcome to Dogdell Park! Plaza is the heart of the park. Four districts around the circle. Travel anytime.",
    ),
    npc(
      "npc-mayor",
      "Mayor Muffin",
      plaza.x - 14,
      plaza.y + 2,
      "guide",
      "Plaza in the middle. Sports, shows, the pond, and the playground each have a travel pad at the entrance.",
    ),
    { id: "hub-bank", kind: "bank", name: "Doggy Bank", x: plaza.x - 14, y: plaza.y - 4 },
    npc("npc-barksmith", "Barksmith Bo", plaza.x + 12, plaza.y - 6, "barksmith"),
    { id: "hub-forge", kind: "forge", name: "Forge", skillId: "toycraft", x: plaza.x + 14, y: plaza.y - 4 },
    npc(
      "npc-shop",
      "Shopkeep Pip",
      plaza.x + 10,
      plaza.y + 12,
      "shopkeeper",
      "Gather out in the park, craft at the forge. Snack carts wander the districts.",
    ),
    gatherNode("plaza-beg-1", BEG_NODES[0]!, plaza.x - 10, plaza.y + 10),
    gatherNode("plaza-beg-2", BEG_NODES[1]!, plaza.x + 4, plaza.y - 14),
    gatherNode("plaza-sniff-1", FORAGE_NODES[0]!, plaza.x - 16, plaza.y + 6),
    gatherNode("north-sniff-1", FORAGE_NODES[0]!, CX - 10, spawnY - 2),
    gatherNode("north-beg-1", BEG_NODES[0]!, CX + 10, spawnY + 4),

    // Sports
    npc(
      "npc-coach",
      "Coach Corgi",
      sports.x + 10,
      sports.y - 8,
      "guide",
      "Racquet Courts: tennis toward the plaza, basketball toward the rim. Chase toys, beg the bleachers.",
    ),
    npc(
      "npc-ref",
      "Ref Ruff",
      basket.x - 10,
      basket.y + 4,
      "guide",
      "Hoops side. Soft balls and sticks for Fetching. Please don't dig the court.",
    ),
    npc("npc-hotdog", "Hotdog Hank", sports.x + 12, sports.y + 12, "vendor", "Park dogs and park dogs. Both welcome."),
    gatherNode("sport-fetch-twig-1", FETCH_NODES[0]!, basket.x + 4, basket.y - 14),
    gatherNode("sport-fetch-stick-1", FETCH_NODES[1]!, sports.x + 10, sports.y + 14),
    gatherNode("sport-fetch-hard-1", FETCH_NODES[2]!, sports.x - 4, sports.y + 16),
    gatherNode("sport-fetch-maple-1", FETCH_NODES[3]!, sports.x + 14, sports.y - 2),
    // Bleacher beg / sniff stay west of the basketball asphalt (not on the pad).
    gatherNode("sport-beg-1", BEG_NODES[1]!, basket.x - 10, basket.y - 6),
    gatherNode("sport-beg-2", BEG_NODES[2]!, sports.x + 14, sports.y - 16),
    gatherNode("sport-sniff-1", FORAGE_NODES[0]!, basket.x - 12, basket.y + 12),

    // Amphitheater
    npc(
      "npc-amp",
      "Amp Annie",
      amph.x - 24,
      amph.y - 20,
      "guide",
      "You're at the amphitheater! Shakespeare on the lawn - spread a blanket, keep the aisle clear.",
    ),
    npc(
      "npc-amp-usher",
      "Usher Uma",
      amph.x - 20,
      amph.y - 24,
      "guide",
      "Center aisle stays open for the show. Benches face the stage. Quiet paws during the encore.",
    ),
    npc(
      "npc-amp-busker",
      "Busker Beau",
      amph.x + 28,
      amph.y - 8,
      "guide",
      "Open mic for good dogs. I throw crumbs when the crowd claps.",
    ),
    npc("npc-pretzel", "Pretzel Pat", amph.x - 28, amph.y - 6, "vendor", "Salted. Twisted. Slightly judgmental of diggers."),
    npc("npc-popcorn", "Pip Popcorn", amph.x + 28, amph.y - 16, "vendor", "Show snacks. Extra butter for good dogs."),
    npc(
      "npc-hamlet",
      "Sir Barksalot",
      amph.x - 3,
      amph.y + 15,
      "performer",
      "Tonight: Hamlet, Romeo, Macbeth - with a few extra woofs. Quiet paws in the front row.",
    ),
    npc(
      "npc-juliet",
      "Lady Yapulet",
      amph.x + 3,
      amph.y + 15,
      "performer",
      "Juliet, Lady Macbeth, and more. Real lines. Dog cameos. No tennis balls as Yorick - usually.",
    ),
    npc(
      "npc-understudy",
      "The Understudy",
      amph.x,
      amph.y + 14,
      "performer",
      "Horatio, Puck, Brutus - whoever's free. Cue-to-cue, one voice at a time.",
    ),
    gatherNode("amp-beg-1", BEG_NODES[2]!, amph.x - 26, amph.y - 14),
    gatherNode("amp-beg-2", BEG_NODES[3]!, amph.x + 26, amph.y - 12),
    gatherNode("amp-beg-3", BEG_NODES[4]!, amph.x - 30, amph.y - 2),
    gatherNode("amp-sniff-1", FORAGE_NODES[0]!, amph.x + 24, amph.y - 22),
    gatherNode("amp-sniff-2", FORAGE_NODES[1]!, amph.x - 28, amph.y - 20),
    gatherNode("amp-sniff-3", FORAGE_NODES[3]!, amph.x + 30, amph.y + 4),
    ...amphitheaterCrowd(amph),

    // Pond
    npc(
      "npc-gardener",
      "Gardener Gert",
      pondQ.x + 38,
      pondQ.y + 34,
      "guide",
      "Pond loop. Nose-down for snacks: crumbs to royal truffles. Picnic sitters love a Beg.",
    ),
    npc(
      "npc-duckwatch",
      "Wally Watch",
      Math.round(pondQ.x - POND_RX - 5),
      Math.round(pondQ.y + 8),
      "guide",
      "Folks fish and toss crumbs. Ducks don't share. Sniff the lawn.",
    ),
    npc(
      "npc-fisher",
      "Fisher Fay",
      pondQ.x + 10,
      pondQ.y - 38,
      "guide",
      "Quiet cast, quiet paws. Feed the fish if you must, but leave the bait alone.",
    ),
    npc("npc-icecream", "Connie Cone", pondQ.x - 34, pondQ.y - 30, "vendor", "Soft-serve by the water. Don't drip on the ducks."),
    gatherNode("lake-crumb-1", FORAGE_NODES[0]!, pondQ.x - 44, pondQ.y + 16),
    gatherNode("lake-crumb-2", FORAGE_NODES[0]!, pondQ.x + 44, pondQ.y - 12),
    gatherNode("lake-berry-1", FORAGE_NODES[1]!, pondQ.x - 10, pondQ.y + 44),
    gatherNode("lake-berry-2", FORAGE_NODES[1]!, pondQ.x + 34, pondQ.y - 34),
    gatherNode("lake-apple-1", FORAGE_NODES[2]!, pondQ.x - 44, pondQ.y - 16),
    gatherNode("lake-cheese-1", FORAGE_NODES[3]!, pondQ.x + 44, pondQ.y + 22),
    gatherNode("lake-picnic-1", FORAGE_NODES[4]!, pondQ.x, pondQ.y + 44),
    gatherNode("lake-gourmet-1", FORAGE_NODES[5]!, pondQ.x - 34, pondQ.y + 38),
    gatherNode("lake-truffle-1", FORAGE_NODES[6]!, pondQ.x + 38, pondQ.y + 12),
    gatherNode("lake-beg-1", BEG_NODES[2]!, pondQ.x + 28, pondQ.y - 38),
    gatherNode("lake-beg-2", BEG_NODES[3]!, Math.round(pondQ.x - POND_RX - 4), Math.round(pondQ.y + 34)),
    gatherNode("lake-crumb-3", FORAGE_NODES[0]!, pondQ.x + 20, pondQ.y + 42),
    gatherNode("lake-berry-3", FORAGE_NODES[1]!, pondQ.x - 40, pondQ.y - 8),
    gatherNode("lake-beg-3", BEG_NODES[0]!, pondQ.x + 42, pondQ.y + 8),

    // Playground
    npc(
      "npc-digger",
      "Digger Dot",
      play.x - 6,
      play.y - 4,
      "guide",
      "Playground sand. Dig soft mounds for pebbles and bones. Watch sticky fingers near the swings.",
    ),
    npc(
      "npc-parent",
      "Parent Pam",
      play.x + 12,
      play.y + 8,
      "guide",
      "Kids love good dogs. Sit for pets, but keep your pack zipped. Sticky fingers are real.",
    ),
    npc(
      "npc-sitter",
      "Petting Pat",
      play.x + 4,
      play.y - 12,
      "guide",
      "Easy pets here if you behave. Beg polite. Dig only in the sand pits.",
    ),
    npc(
      "npc-nanny",
      "Nanny Nell",
      play.x + 16,
      play.y - 2,
      "guide",
      "Kennels unlock after your first digs. Open Travel for home and Kennel Row.",
    ),
    npc("npc-balloon", "Balloony Bea", play.x - 12, play.y + 10, "vendor", "Floaty bones on strings."),
    gatherNode("dig-pebble-1", DIG_NODES[0]!, play.x - 6, play.y),
    gatherNode("dig-pebble-2", DIG_NODES[0]!, play.x + 8, play.y + 6),
    gatherNode("dig-bone-1", DIG_NODES[1]!, play.x - 2, play.y + 8),
    gatherNode("dig-bone-2", DIG_NODES[1]!, play.x + 6, play.y - 6),
    gatherNode("dig-iron-1", DIG_NODES[2]!, play.x - 12, play.y + 2),
    gatherNode("dig-iron-2", DIG_NODES[2]!, play.x + 12, play.y + 4),
    gatherNode("dig-charcoal-1", DIG_NODES[3]!, play.x - 8, play.y + 12),
    gatherNode("dig-silver-1", DIG_NODES[4]!, play.x + 2, play.y - 10),
    gatherNode("play-beg-1", BEG_NODES[0]!, play.x - 4, play.y + 14),
    gatherNode("play-beg-2", BEG_NODES[4]!, play.x + 14, play.y + 6),
    gatherNode("play-sniff-1", FORAGE_NODES[0]!, play.x - 14, play.y - 4),
    gatherNode("fetch-twig-1", FETCH_NODES[0]!, play.x - 14, play.y + 6),
    gatherNode("fetch-stick-1", FETCH_NODES[1]!, play.x + 14, play.y - 8),

    // Mid-lawn interest between districts (not stacked on pads).
    npc(
      "npc-jogger",
      "Jogger Jess",
      CX + 22,
      CY - 34,
      "guide",
      "Keep moving. The best smells are between the pads, not on them.",
    ),
    npc(
      "npc-picnic",
      "Picnic Paul",
      CX - 30,
      CY + 26,
      "guide",
      "Blanket's open. Sniff polite crumbs. Don't sit on the sandwiches.",
    ),
    npc(
      "npc-birdwatch",
      "Birdie Bea",
      CX + 34,
      CY + 22,
      "guide",
      "Quiet paws. Pigeons gossip louder than dogs think.",
    ),
    // Ambient park pups - different breeds via client hash on id.
    npc("pup-biscuit", "Biscuit", CX - 16, CY - 22, "guide", "Just out for a polite trot. Don't mind me."),
    npc("pup-nimbus", "Nimbus", CX + 18, CY - 28, "guide", "Cloud-watching is a skill. I'm almost maxed."),
    npc("pup-pebble", "Pebble", CX + 8, CY + 24, "guide", "I collect interesting rocks. Professionally."),
    npc("pup-mocha", "Mocha", CX - 24, CY + 8, "guide", "Is that treatcraft I smell, or wishful thinking?"),
    npc("pup-sprout", "Sprout", CX + 28, CY + 6, "guide", "Grow fast, dig faster. That's my motto."),
    npc("pup-echo", "Echo", CX - 8, CY - 42, "guide", "I barked once and the plaza answered. Spooky."),
    npc("pup-cork", "Cork", CX + 42, CY - 16, "guide", "Floaty brain. Serious legs. Let's go."),
    npc("pup-fig", "Fig", CX - 40, CY - 12, "guide", "Sweet as a picnic, sticky as a dig."),
    npc("pup-willow", "Willow", CX + 16, CY + 38, "guide", "Bend with the breeze. Never skip a sniff."),
    npc("pup-rusty", "Rusty", CX - 18, CY + 36, "guide", "Old collar, young heart. Race you to the pad."),
    npc("pup-cosmo", "Cosmo", sports.x - 10, sports.y + 10, "guide", "Sports day every day. Stretch those haunches."),
    npc("pup-zing", "Zing", sports.x + 6, sports.y - 12, "guide", "Tennis balls are my love language."),
    npc("pup-opal", "Opal", amph.x - 24, amph.y - 18, "guide", "I'm here for the drama. And the popcorn."),
    npc("pup-quill", "Quill", amph.x + 24, amph.y - 4, "guide", "Critics say I lack stage presence. Rude."),
    npc("pup-ripple", "Ripple", pondQ.x + 28, pondQ.y - 34, "guide", "Ducks have the best gossip. Don't tell them I said that."),
    npc("pup-kelp", "Kelp", pondQ.x - 24, pondQ.y + 34, "guide", "Shoreline patrol. Official. Self-appointed."),
    npc("pup-dune", "Dune", play.x + 8, play.y + 8, "guide", "Sand in the fur builds character."),
    npc("pup-tumble", "Tumble", play.x - 10, play.y - 6, "guide", "I meant to do that roll. Mostly."),
    npc("pup-north", "Northstar", CX + 4, spawnY - 6, "guide", "Welcome wagon on four paws. You've got this."),
    npc("pup-plaza", "Plaza Pip", plaza.x + 10, plaza.y + 10, "guide", "Heart of the park. Best people-watching seats."),
    // Extra roamers - fill lawns, cookouts, drums, and quiet corners.
    npc("pup-marzipan", "Marzipan", CX - 12, CY + 18, "guide", "Soft steps only. I'm practicing stealth treats."),
    npc("pup-bolt", "Bolt", CX + 22, CY - 8, "guide", "If I run in a circle, is that cardio or a dance?"),
    npc("pup-truffle", "Truffle", CX - 28, CY - 30, "guide", "I smell something fancy. Or mud. Probably mud."),
    npc("pup-juno", "Juno", CX + 10, CY + 32, "guide", "Orbiting the plaza. Permanent vacation mode."),
    npc("pup-basil", "Basil", CX - 44, CY + 8, "guide", "Herb garden energy. Very sniffable."),
    npc("pup-comet", "Comet", CX + 44, CY - 24, "guide", "Fast greetings! Bye! Wait - hi again!"),
    npc("pup-drizzle", "Drizzle", pondQ.x + 18, pondQ.y + 28, "guide", "Pier paws. Do not recommend for socks."),
    npc("pup-pebble2", "Skipper", pondQ.x - 36, pondQ.y - 20, "guide", "Skipping stones is a sport. I'm undefeated."),
    npc("pup-rhythm", "Rhythm", drumAt.x - 6, drumAt.y + 6, "guide", "Got the beat. Lost the lyrics. Still dancing."),
    npc("pup-tempo", "Tempo", drumAt.x + 8, drumAt.y - 4, "guide", "Tail-wag metronome. Hire me for shows."),
    npc("pup-sizzle", "Sizzle", cookouts[2]!.x - 4, cookouts[2]!.y + 5, "guide", "Charcoal perfume. Don't tell the grill."),
    npc("pup-mayo", "Mayo", cookouts[3]!.x + 4, cookouts[3]!.y - 4, "guide", "Picnic security. I taste-test for safety."),
    npc("pup-brisket", "Brisket", cookouts[4]!.x - 5, cookouts[4]!.y + 4, "guide", "BBQ meadow VIP. The VIP is me."),
    npc("pup-skewer", "Skewer", cookouts[0]!.x + 3, cookouts[0]!.y - 5, "guide", "I follow the smoke. Professionally."),
    npc("pup-coleslaw", "Coleslaw", cookouts[1]!.x - 3, cookouts[1]!.y + 4, "guide", "Side dish energy. Main character heart."),
    npc("pup-ember", "Ember", cookouts[6]!.x + 5, cookouts[6]!.y + 2, "guide", "North grill. Cool breeze. Hot tips."),
    npc("pup-patty", "Patty", cookouts[5]!.x - 4, cookouts[5]!.y + 3, "guide", "Lakeside grease. Worth the walk."),
    npc("pup-relish", "Relish", cookouts[4]!.x + 6, cookouts[4]!.y - 3, "guide", "Condiment diplomacy. I'm Switzerland."),
    npc("pup-ace", "Ace", sports.x + 12, sports.y - 8, "guide", "Love a good rally. Also love naps after."),
    npc("pup-volley", "Volley", sports.x - 6, sports.y + 12, "guide", "I almost caught that. Emotionally."),
    npc("pup-encore", "Encore", amph.x + 22, amph.y - 18, "guide", "One more bow. Then snacks. Then another bow."),
    npc("pup-sandbox", "Sandbox", play.x - 6, play.y + 10, "guide", "Dig first. Ask questions never."),
    npc("pup-pretzel", "Pretzel", CX + 6, CY - 20, "guide", "Twisty thoughts. Straight path. Usually."),
    npc("pup-maple", "Maple", CX - 20, CY + 28, "guide", "Fall leaves? Wrong season. Still cute."),
    npc("pup-wisp", "Wisp", CX + 32, CY + 14, "guide", "Here, then there, then treat. Perfect day."),
    // Second pack - fill mid-ring lawns, picnic meadows, and quiet path stretches.
    npc("pup-bean", "Bean", CX - 6, CY - 30, "guide", "Small dog. Big itinerary."),
    npc("pup-churro", "Churro", CX + 14, CY - 36, "guide", "Cinnamon on the breeze. Or dirt. Close enough."),
    npc("pup-latte", "Latte", CX + 34, CY - 6, "guide", "Foam optional. Walks mandatory."),
    npc("pup-mochi", "Mochi", CX + 26, CY + 26, "guide", "Soft landings only. I'm practicing."),
    npc("pup-pepper", "Pepper", CX - 30, CY + 22, "guide", "Sniff first. Ask questions second. Maybe."),
    npc("pup-scooter", "Scooter", CX - 34, CY - 18, "guide", "I don't have wheels. I have determination."),
    npc("pup-taffy", "Taffy", CX + 4, CY + 44, "guide", "Sticky situations are my specialty."),
    npc("pup-waffles", "Waffles", CX - 14, CY + 48, "guide", "Grid pattern optional. Butter required."),
    npc("pup-noodle", "Noodle", CX + 48, CY + 2, "guide", "Long thoughts. Short legs. Still winning."),
    npc("pup-bandit", "Bandit", CX - 50, CY - 4, "guide", "I steal hearts. And sometimes dropped treats."),
    npc("pup-daisy", "Daisy", CX + 20, CY - 48, "guide", "Flower power. Also dig power."),
    npc("pup-pickle", "Pickle", CX - 22, CY - 52, "guide", "Sour mood? Not me. I'm briny with joy."),
    npc("pup-remy", "Remy", CX + 20, CY - 40, "guide", "Picnic critic. Five paws if there's cheese."),
    npc("pup-scout", "Scout", CX - 16, CY - 46, "guide", "Perimeter check complete. Finding nothing. Finding everything."),
    npc("pup-sprinkles", "Sprinkles", CX + 46, CY + 16, "guide", "Life needs toppings. Especially walks."),
    npc("pup-toast", "Toast", CX - 46, CY - 6, "guide", "Warm. Crunchy. Slightly buttered attitude."),
    npc("pup-honey", "Honey", CX + 34, CY + 36, "guide", "Sweet as a sit. Sticky as a hug."),
    npc("pup-olive", "Olive", pondQ.x + 32, pondQ.y + 28, "guide", "Martini energy. Sober paws."),
    npc("pup-pixel", "Pixel", play.x + 16, play.y + 14, "guide", "I see the world in treats per square."),
    npc("pup-roo", "Roo", sports.x + 16, sports.y + 4, "guide", "Hop? No. Trott. Same vibe though."),
    npc("pup-sage", "Sage", sports.x - 14, sports.y - 6, "guide", "Wise enough to rest between fetch sets."),
    npc("pup-teddy", "Teddy", amph.x - 26, amph.y - 2, "guide", "Soft applause only. I'm sensitive."),
    npc("pup-velvet", "Velvet", amph.x + 26, amph.y - 16, "guide", "Red carpet? Green grass. Close enough."),
    npc("pup-zippy", "Zippy", pondQ.x + 10, pondQ.y - 40, "guide", "Fast hello. Faster goodbye. Wait - hello!"),
    npc("pup-pancake", "Pancake", pondQ.x - 30, pondQ.y + 20, "guide", "Flat on the pier is peak relaxation."),
    npc("pup-clover", "Clover", play.x + 14, play.y - 10, "guide", "Lucky digs. Unlucky sand in the ears."),
    npc("pup-miso", "Miso", play.x - 14, play.y + 4, "guide", "Umami mood. Very park."),
    npc("pup-peanut", "Peanut", CX + 54, CY - 32, "guide", "Tiny but mighty. Ask the ducks."),
    npc("pup-rolo", "Rolo", CX - 54, CY + 18, "guide", "Round of applause for round pups."),
    npc("pup-socks", "Socks", CX + 12, CY + 52, "guide", "Four socks. Zero laundry days."),
    npc("pup-twinkle", "Twinkle", CX - 10, CY - 58, "guide", "Evening sparkle starts at lunch."),
    npc("pup-yuzu", "Yuzu", CX + 58, CY + 24, "guide", "Zesty greetings. Mild side-eye."),
    npc("pup-zephyr", "Zephyr", CX - 58, CY - 28, "guide", "I go where the breeze suggests."),
    npc("pup-cricket", "Cricket", sportsGate.x - 8, sportsGate.y + 6, "guide", "Gate greeter. Unofficial. Highly waggy."),
    npc("pup-fluff", "Fluff", amphGate.x + 6, amphGate.y - 6, "guide", "Show traffic control. Soft barriers only."),
    npc("pup-nugget", "Nugget", pondGate.x - 6, pondGate.y + 8, "guide", "Pond VIP. The P stands for paws."),
    npc("pup-piper", "Piper", playGate.x + 8, playGate.y + 4, "guide", "Playground liaison. Sand negotiator."),
    npc("pup-sunny", "Sunny", cookouts[0]!.x + 10, cookouts[0]!.y + 8, "guide", "Frisbee outfield. Emotional support catcher."),
    npc("pup-tippy", "Tippy", cookouts[1]!.x + 10, cookouts[1]!.y + 8, "guide", "Grove outfield. I tip over for dramatic effect."),
    npc("pup-biscuit2", "Crumb", cookouts[2]!.x + 10, cookouts[2]!.y + 8, "guide", "East lawn crumbs are legally mine."),
    npc("pup-button", "Button", cookouts[3]!.x + 10, cookouts[3]!.y + 8, "guide", "West pavilion button-cute. Official."),
    npc("pup-cupcake", "Cupcake", cookouts[4]!.x + 10, cookouts[4]!.y + 8, "guide", "South BBQ frosting optional."),
    npc("pup-fudge", "Fudge", cookouts[5]!.x + 10, cookouts[5]!.y + 8, "guide", "Lakeside dessert energy. Don't tell Connie."),
    npc("pup-gumdrop", "Gumdrop", cookouts[6]!.x + 10, cookouts[6]!.y + 8, "guide", "North meadow chewy. In a good way."),
    npc("pup-hazel", "Hazel", drumAt.x - 12, drumAt.y - 8, "guide", "Drum circle backup dancer. Fur edition."),
    npc("pup-indigo", "Indie", drumAt.x + 14, drumAt.y + 6, "guide", "Independent artist. Medium fame. High wag."),
    npc("pup-jam", "Jam", CX + 40, CY - 44, "guide", "Stuck on this lawn. Happily."),
    npc("pup-kiwi", "Kiwi", CX - 42, CY + 40, "guide", "Fuzzy outside. Adventure inside."),
    npc("pup-lemon", "Lemon", CX + 8, CY + 58, "guide", "Bright attitude. Mild pulp."),
    npc("pup-mint", "Mint", CX - 8, CY - 64, "guide", "Fresh start every sniff."),
    gatherNode("lawn-beg-1", BEG_NODES[1]!, CX + 36, CY - 18),
    gatherNode("lawn-beg-2", BEG_NODES[0]!, CX - 36, CY - 20),
    gatherNode("lawn-beg-3", BEG_NODES[2]!, CX + 18, CY + 40),
    gatherNode("lawn-beg-4", BEG_NODES[3]!, CX - 20, CY + 42),
    // Cookouts far on the rim - mixed beg poses + frisbee lawns nearby.
    ...cookouts.flatMap((spot, i) => {
      const cookType = BEG_NODES[(4 + (i % 3)) % BEG_NODES.length]!;
      const eatType = BEG_NODES[(2 + i) % BEG_NODES.length]!;
      const chatType = BEG_NODES[i % BEG_NODES.length]!;
      const sitType = BEG_NODES[(1 + i) % BEG_NODES.length]!;
      const cookNames = ["Grill Greta", "Skillet Sam", "Tongs Tess", "Smoke Sid", "Charcoal Chet", "Ember Em", "BBQ Bea"];
      const eatNames = ["Picnic Pip", "Snack Sal", "Nibble Nell", "Crumbs Kit", "Plate Pat", "Napkin Nan", "Fork Fay"];
      const chatNames = ["Chatty Chet", "Gabby Gail", "Yarn Yves", "Story Stu", "Ramble Remy", "Banter Bea", "Gossip Gil"];
      const sitNames = ["Lounge Lee", "Rest Remy", "Shade Shay", "Quiet Quinn", "Porch Perry", "Idle Ida", "Settle Sue"];
      const poses = (
        [
          { suffix: "cook", type: cookType, name: cookNames[i % cookNames.length]!, pose: "cook" as const, ox: 2, oy: 2 },
          { suffix: "eat", type: eatType, name: eatNames[i % eatNames.length]!, pose: "eat" as const, ox: -2, oy: 0 },
          { suffix: "chat", type: chatType, name: chatNames[i % chatNames.length]!, pose: "chat" as const, ox: 1, oy: -3 },
          { suffix: "sit", type: sitType, name: sitNames[i % sitNames.length]!, pose: "sit" as const, ox: -1, oy: 3 },
        ] as const
      ).filter((_, j) => (i % 2 === 0 ? j !== 3 : j !== 0)); // alternate: skip sit or cook so not everyone cooks
      return poses.map((p) =>
        gatherNode(`bbq-${spot.id}-${p.suffix}`, p.type, spot.x + p.ox, spot.y + p.oy, {
          name: p.name,
          begPose: p.pose,
        }),
      );
    }),
    gatherNode("lawn-sniff-1", FORAGE_NODES[0]!, CX + 30, CY - 40),
    gatherNode("lawn-sniff-2", FORAGE_NODES[1]!, CX - 32, CY - 36),
    gatherNode("lawn-sniff-3", FORAGE_NODES[0]!, CX + 40, CY + 8),
    gatherNode("lawn-sniff-4", FORAGE_NODES[2]!, CX - 42, CY + 10),
    gatherNode("lawn-sniff-5", FORAGE_NODES[3]!, CX + 8, CY + 48),
    gatherNode("lawn-fetch-1", FETCH_NODES[0]!, CX - 8, CY + 46),
    gatherNode("lawn-fetch-2", FETCH_NODES[1]!, CX + 44, CY - 8),
    gatherNode("lawn-dig-1", DIG_NODES[0]!, CX - 24, CY + 50),
    gatherNode("lawn-dig-2", DIG_NODES[1]!, CX + 26, CY + 46),
  ];

  const pad = (id: string, hub: { x: number; y: number }, label: string): TeleporterDef => ({
    id,
    x: hub.x - 2,
    y: hub.y - 2,
    w: 5,
    h: 5,
    tag: "__TravelMenu__",
    label,
  });
  const teleporters: TeleporterDef[] = [
    pad("tp-plaza", plaza, "Community Plaza"),
    pad("tp-sports", sportsGate, "Racquet Courts"),
    pad("tp-amph", amphGate, "Amphitheater"),
    pad("tp-pond", pondGate, "Park Pond"),
    pad("tp-play", playGate, "Playground"),
  ];

  const markers: GroundMarkerDef[] = [
    // Large welcome pad north of plaza; players land in an inner circle inside it.
    { id: "hub-spawn", kind: "spawn", x: CX - 3, y: spawnY - 3, w: 7, h: 7 },
  ];

  const scenery: SceneryDef[] = [
    { id: "drum-circle", kind: "drumCircle", x: drumAt.x, y: drumAt.y },
    ...cookouts.flatMap((spot) => [
      { id: `pav-${spot.id}`, kind: "pavilion" as const, x: spot.x, y: spot.y },
      { id: `grill-${spot.id}`, kind: "bbqGrill" as const, x: spot.x + 3, y: spot.y + 2 },
      { id: `table-${spot.id}`, kind: "picnicTable" as const, x: spot.x - 2, y: spot.y + 1 },
      // Meadow toss - well outside the pavilion roof and tree line.
      { id: `frisbee-${spot.id}`, kind: "frisbeeGame" as const, x: spot.x + 16, y: spot.y + 14 },
    ]),
    { id: "hoop-n", kind: "basketHoop", x: basket.x, y: basket.y - 10, rotY: 0 },
    { id: "hoop-s", kind: "basketHoop", x: basket.x, y: basket.y + 10, rotY: Math.PI },
    { id: "net-tennis-1", kind: "tennisNet", x: sports.x, y: sports.y - 5 },
    { id: "net-tennis-2", kind: "tennisNet", x: sports.x, y: sports.y + 5 },
    // Court-yard lamp in the grass alley between basketball (west) and tennis (east).
    {
      id: "sports-lamp-alley",
      kind: "lamp" as const,
      x: Math.round((basket.x + 6 + sports.x - 7) * 0.5),
      y: sports.y,
    },
    { id: "stand-hotdog", kind: "hotdogStand", x: sports.x + 12, y: sports.y + 13 },
    { id: "stand-ice", kind: "iceCreamCart", x: pondQ.x - 34, y: pondQ.y - 32 },
    { id: "stand-pretzel", kind: "pretzelCart", x: amph.x - 30, y: amph.y - 4 },
    { id: "stand-popcorn", kind: "popcornCart", x: amph.x + 30, y: amph.y - 16 },
    { id: "stand-balloon", kind: "balloonCart", x: play.x - 12, y: play.y + 11 },
    { id: "amp-stage", kind: "stage", x: amph.x, y: amph.y + 16, yLift: -0.22 },
    ...amphitheaterPicnicScenery(amph),
    { id: "pond-bench-1", kind: "bench", x: pondQ.x - 40, y: pondQ.y - 16, rotY: 0.6 },
    { id: "pond-bench-2", kind: "bench", x: pondQ.x + 40, y: pondQ.y + 12, rotY: -0.6 },
    { id: "pond-bench-3", kind: "bench", x: Math.round(pondQ.x + 8), y: Math.round(pondQ.y + POND_RY + 10) },
    { id: "pond-bench-4", kind: "bench", x: pondQ.x + 28, y: pondQ.y - 38, rotY: -0.3 },
    { id: "pond-bench-5", kind: "bench", x: Math.round(pondQ.x - POND_RX - 6), y: Math.round(pondQ.y + 28), rotY: 0.4 },
    { id: "pond-bench-6", kind: "bench", x: pondQ.x + 36, y: pondQ.y - 20, rotY: -0.5 },
    { id: "play-bench-1", kind: "bench", x: play.x - 6, y: play.y + 14 },
    { id: "play-bench-2", kind: "bench", x: play.x + 8, y: play.y + 12 },
    { id: "play-bench-3", kind: "bench", x: play.x + 14, y: play.y - 4, rotY: -0.5 },
    // Walkable playground gear on the rubber court.
    { id: "play-swing", kind: "swingSet", x: play.x - 4, y: play.y - 2 },
    { id: "play-slide", kind: "slide", x: play.x + 6, y: play.y + 3, rotY: -0.55 },
    { id: "play-climber", kind: "climber", x: play.x + 1, y: play.y - 5 },
    { id: "play-climber-2", kind: "climber", x: play.x - 6, y: play.y + 3, rotY: 0.7 },
    { id: "plaza-bench-1", kind: "bench", x: plaza.x - 15, y: plaza.y + 6, rotY: Math.PI * 0.5 },
    { id: "plaza-bench-2", kind: "bench", x: plaza.x + 15, y: plaza.y - 2, rotY: -Math.PI * 0.5 },
    { id: "plaza-bench-3", kind: "bench", x: plaza.x - 4, y: plaza.y - 15 },
    // Keep welcome-pad clear - sit north of the spawn footprint, not on it.
    { id: "north-bench-1", kind: "bench", x: CX - 10, y: spawnY + 8, rotY: Math.PI },
    { id: "north-bench-2", kind: "bench", x: CX + 10, y: spawnY + 8, rotY: Math.PI },
    { id: "lawn-bench-ne", kind: "bench", x: CX + 34, y: CY - 48, rotY: -0.4 },
    { id: "lawn-bench-nw", kind: "bench", x: CX - 30, y: CY - 34, rotY: 0.4 },
    { id: "lawn-bench-e", kind: "bench", x: CX + 42, y: CY + 4, rotY: -Math.PI * 0.5 },
    { id: "lawn-bench-w", kind: "bench", x: CX - 42, y: CY + 2, rotY: Math.PI * 0.5 },
    { id: "lawn-bench-s", kind: "bench", x: CX + 6, y: CY + 46 },
    { id: "lawn-bench-se", kind: "bench", x: CX + 32, y: CY + 28, rotY: -0.3 },
    // Sideline bleachers - well clear of basketball / tennis asphalt.
    { id: "sports-bench-1", kind: "bench", x: sports.x + 16, y: sports.y + 16 },
    { id: "sports-bench-2", kind: "bench", x: basket.x - 14, y: basket.y - 12, rotY: Math.PI * 0.5 },
    // Lawn picnics: families, dates, and friend groups (keep clear of sports asphalt).
    { id: "picnic-family-ne", kind: "picnic", x: CX + 38, y: CY - 52 },
    { id: "picnic-date-n", kind: "picnic", x: CX - 18, y: CY - 48 },
    { id: "picnic-friends-e", kind: "picnic", x: CX + 52, y: CY + 22 },
    { id: "picnic-family-w", kind: "picnic", x: CX - 48, y: CY - 8 },
    { id: "picnic-date-se", kind: "picnic", x: CX + 36, y: CY + 38 },
    // South shore lawn - keep clear of the pond water ellipse.
    {
      id: "picnic-friends-sw",
      kind: "picnic",
      x: Math.round(pondQ.x + 18),
      y: Math.round(pondQ.y + POND_RY + 12),
    },
    { id: "picnic-family-pond", kind: "picnic", x: pondQ.x + 34, y: pondQ.y + 30 },
    { id: "picnic-date-play", kind: "picnic", x: play.x + 18, y: play.y + 16 },
    // Irrigation pipes live only on the east splash playground.
    ...lawnSprayerScenery(splashPads),
  ];

  // Nudge people off water/forest and cut vegetation under picnic / cookout pads.
  ensurePeopleClearings(tiles, w, h, nodes, scenery);
  keepFrisbeeInOpenMeadow(tiles, w, h, scenery);
  for (const item of scenery) {
    if (item.kind === "picnic") {
      const amph = item.id.startsWith("amp-picnic");
      // Open lawn under the blanket; amph blankets skip outer flower accents so clusters stay tight.
      softLawnBlob(tiles, w, h, item.x, item.y, amph ? 2.4 : 5.5, amph ? 2.0 : 4.5, Tile.Grass);
      if (!amph) {
        softLawnBlob(tiles, w, h, item.x + 8, item.y - 6, 2, 1.8, Tile.Flowers, 0.4);
        softLawnBlob(tiles, w, h, item.x - 8, item.y + 5, 1.8, 1.6, Tile.Flowers, 0.4);
      }
      clearPeoplePad(tiles, w, h, item.x, item.y, amph ? 2.6 : 5.5, amph ? 2.2 : 4.5);
    }
  }

  lakeBridgeRails(tiles, w, h, scenery, pondQ.x, pondQ.y, POND_RX, POND_RY);
  lakePierFishermen(scenery, pondQ.x, pondQ.y, POND_RX, POND_RY);

  // Re-stamp court asphalt after décor so flowers / aprons never leave holes in the pads.
  const basketX0 = basket.x - 6;
  const basketX1 = basket.x + 5;
  const basketY0 = basket.y - 10;
  const basketY1 = basket.y + 10;
  fillRect(tiles, w, h, basketX0, basketY0, basketX1, basketY1, Tile.CourtBasket);
  fillRect(tiles, w, h, sports.x - 7, sports.y - 5 - 4, sports.x + 8, sports.y - 5 + 4, Tile.CourtTennis);
  fillRect(tiles, w, h, sports.x - 7, sports.y + 5 - 4, sports.x + 8, sports.y + 5 + 4, Tile.CourtTennis);
  // Keep canopies / flower beds from hanging onto the play surface.
  clearCourtApron(tiles, w, h, basketX0, basketY0, basketX1, basketY1, 3);
  clearCourtApron(tiles, w, h, sports.x - 7, sports.y - 9, sports.x + 8, sports.y - 1, 3);
  clearCourtApron(tiles, w, h, sports.x - 7, sports.y + 1, sports.x + 8, sports.y + 9, 3);
  // Soft lawn blobs can briefly overwrite asphalt before this stamp - re-clip ribbons now.
  pathRibbonsDry = clipAllRibbonsOffBlocked(tiles, w, h, pathRibbonsDry);
  // Picnics / benches that drifted onto asphalt after lawn blobs get pushed off.
  for (const item of scenery) {
    if (item.kind !== "picnic" && item.kind !== "bench" && item.kind !== "lamp") {
      continue;
    }
    if (!isHazardForPeople(getTile(tiles, w, h, Math.round(item.x), Math.round(item.y)))) {
      continue;
    }
    const safe =
      item.kind === "picnic"
        ? nearestPeoplePad(tiles, w, h, item.x, item.y, 5.5, 4.5)
        : nearestPeopleTile(tiles, w, h, item.x, item.y);
    item.x = safe.x;
    item.y = safe.y;
  }
  // Crumbs / twigs / dig piles never sit on asphalt.
  ensureGatherOffCourts(tiles, w, h, nodes);
  // Benches, carts, sitters, and piles stay off travel portals + spawn pad.
  ensureClearOfPortals(tiles, w, h, nodes, scenery, teleporters, markers);

  // Never leave irrigation pipes on court asphalt or inside the amphitheater bowl.
  for (let i = scenery.length - 1; i >= 0; i--) {
    const item = scenery[i]!;
    if (item.kind !== "lawnSprayer") {
      continue;
    }
    const tile = getTile(tiles, w, h, item.x, item.y);
    if (tile === Tile.CourtBasket || tile === Tile.CourtTennis || tile === Tile.CourtLine) {
      scenery.splice(i, 1);
      continue;
    }
    if (Math.hypot(item.x - amph.x, item.y - amph.y) < 44) {
      scenery.splice(i, 1);
    }
  }

  // Strip dense flower beds out of crowded hubs; quiet meadows keep the blooms.
  clearFlowersNearBusy(tiles, w, h, [
    { x: plaza.x, y: plaza.y, r: 14, keep: 0.55 },
    { x: amph.x, y: amph.y + 4, r: 38, keep: 0.06 },
    { x: sports.x, y: sports.y, r: 22, keep: 0.1 },
    { x: basket.x, y: basket.y, r: 16, keep: 0.1 },
    { x: play.x, y: play.y, r: 20, keep: 0.1 },
    { x: splashPlay.x, y: splashPlay.y, r: 18, keep: 0.12 },
    { x: drumAt.x, y: drumAt.y, r: 12, keep: 0.1 },
    { x: CX, y: spawnY, r: 14, keep: 0.08 },
  ]);

  const critters: AmbientCritterDef[] = [
    { id: "sq-plaza-1", kind: "squirrel", homeX: plaza.x - 16, homeY: plaza.y - 8, roam: 7 },
    // Plaza flock - land on the grass collar, take off when pups get close.
    { id: "pg-plaza-1", kind: "pigeon", homeX: plaza.x + 12, homeY: plaza.y + 8, roam: 9 },
    { id: "pg-plaza-2", kind: "pigeon", homeX: plaza.x - 11, homeY: plaza.y + 10, roam: 9 },
    { id: "pg-plaza-3", kind: "pigeon", homeX: plaza.x + 9, homeY: plaza.y - 12, roam: 8 },
    { id: "pg-plaza-4", kind: "pigeon", homeX: plaza.x - 13, homeY: plaza.y - 7, roam: 8 },
    { id: "pg-plaza-5", kind: "pigeon", homeX: plaza.x + 16, homeY: plaza.y - 2, roam: 7 },
    { id: "pg-plaza-6", kind: "pigeon", homeX: plaza.x - 8, homeY: plaza.y + 15, roam: 7 },
    { id: "pg-north-1", kind: "pigeon", homeX: CX - 4, homeY: spawnY - 3, roam: 8 },
    { id: "pg-court-1", kind: "pigeon", homeX: sports.x + 6, homeY: sports.y - 10, roam: 7 },
    { id: "sq-sports-1", kind: "squirrel", homeX: basket.x, homeY: basket.y + 14, roam: 6 },
    { id: "pg-amp-1", kind: "pigeon", homeX: amph.x - 26, homeY: amph.y - 8, roam: 5 },
    { id: "pg-amp-2", kind: "pigeon", homeX: amph.x + 26, homeY: amph.y - 10, roam: 5 },
    { id: "rb-pond-1", kind: "rabbit", homeX: pondQ.x - 44, homeY: pondQ.y + 22, roam: 10 },
    { id: "rb-pond-2", kind: "rabbit", homeX: pondQ.x + 40, homeY: pondQ.y - 28, roam: 9 },
    { id: "rb-pond-3", kind: "rabbit", homeX: pondQ.x + 18, homeY: pondQ.y + 42, roam: 8 },
    // Mother + row of ducklings on the south shore (scatter from dogs, then reform).
    {
      id: "dk-family-1",
      kind: "duck",
      homeX: Math.round(pondQ.x + 6),
      homeY: Math.round(pondQ.y + POND_RY + 3),
      roam: 12,
      familySize: 5,
    },
    // A second quieter family on the west bank.
    {
      id: "dk-family-2",
      kind: "duck",
      homeX: Math.round(pondQ.x - POND_RX - 2),
      homeY: Math.round(pondQ.y + 4),
      roam: 10,
      familySize: 4,
    },
    // A few lone ducks still dabble elsewhere around the pond.
    { id: "dk-1", kind: "duck", homeX: pondQ.x - 8, homeY: pondQ.y - 18, roam: 8 },
    { id: "dk-2", kind: "duck", homeX: pondQ.x + 22, homeY: pondQ.y - 8, roam: 9 },
    { id: "dk-3", kind: "duck", homeX: pondQ.x + 8, homeY: pondQ.y + POND_RY + 10, roam: 7 },
    { id: "sq-play-1", kind: "squirrel", homeX: play.x - 14, homeY: play.y - 6, roam: 6 },
    { id: "rb-play-1", kind: "rabbit", homeX: play.x + 12, homeY: play.y + 10, roam: 5 },
    { id: "pg-play-1", kind: "pigeon", homeX: play.x + 2, homeY: play.y + 8, roam: 7 },
    { id: "sq-lawn-ne", kind: "squirrel", homeX: CX + 34, homeY: CY - 28, roam: 8 },
    { id: "sq-lawn-sw", kind: "squirrel", homeX: CX - 36, homeY: CY + 24, roam: 8 },
    { id: "rb-lawn-se", kind: "rabbit", homeX: CX + 28, homeY: CY + 34, roam: 7 },
    { id: "pg-lawn-nw", kind: "pigeon", homeX: CX - 26, homeY: CY - 38, roam: 9 },
    { id: "pg-lawn-e", kind: "pigeon", homeX: CX + 44, homeY: CY + 6, roam: 8 },
  ];

  // Humans walking dogs on open lawns (away from ponds, stage, and travel pads).
  const leashWalkers: AmbientLeashWalkerDef[] = [
    { id: "leash-plaza-n", name: "Mira", dogName: "Biscuit Jr", homeX: plaza.x - 18, homeY: plaza.y - 14, roam: 16 },
    { id: "leash-plaza-e", name: "Jonah", dogName: "Pepper", homeX: plaza.x + 20, homeY: plaza.y + 8, roam: 15 },
    { id: "leash-meadow", name: "Asha", dogName: "Noodle", homeX: CX - 22, homeY: CY + 20, roam: 18 },
    { id: "leash-east", name: "Cole", dogName: "Maple", homeX: CX + 30, homeY: CY - 18, roam: 17 },
    { id: "leash-pond-shore", name: "Elena", dogName: "Kelpie", homeX: pondQ.x + 38, homeY: pondQ.y - 36, roam: 12 },
    { id: "leash-pond-west", name: "Theo", dogName: "Drift", homeX: pondQ.x - 42, homeY: pondQ.y + 18, roam: 13 },
    { id: "leash-sports", name: "Priya", dogName: "Ace", homeX: sports.x - 18, homeY: sports.y + 16, roam: 14 },
    { id: "leash-amph-lawn", name: "Owen", dogName: "Velvet", homeX: amph.x - 32, homeY: amph.y - 22, roam: 12 },
    { id: "leash-play", name: "Nina", dogName: "Tumble", homeX: play.x + 18, homeY: play.y - 14, roam: 13 },
    { id: "leash-cookout", name: "Gabe", dogName: "Sizzle", homeX: cookouts[2]!.x + 10, homeY: cookouts[2]!.y + 8, roam: 11 },
  ];

  return mapOf(
    "hub",
    "Dogdell Park",
    w,
    h,
    tiles,
    nodes,
    teleporters,
    markers,
    scenery,
    critters,
    pathRibbonsDry,
    courtMarks,
    leashWalkers,
  );
}

export function createDigParkMap(): MapData {
  return createHubMap();
}

export function createFetchWoodsMap(): MapData {
  return createHubMap();
}

export function createForgeDenMap(): MapData {
  const w = 56;
  const h = 42;
  const tiles = makeGrid(w, h, Tile.Floor);
  treeBorder(tiles, w, h, 2);
  fillRect(tiles, w, h, 24, 34, 32, 38, Tile.Path);
  fillRect(tiles, w, h, 4, 4, 10, 10, Tile.Rock);
  fillRect(tiles, w, h, 44, 4, 50, 10, Tile.Rock);
  fillRect(tiles, w, h, 4, 28, 10, 34, Tile.Rock);
  fillRect(tiles, w, h, 44, 28, 50, 34, Tile.Rock);
  paddock(tiles, w, h, 18, 8, 38, 22, 28, 22);
  fillRect(tiles, w, h, 24, 22, 32, 34, Tile.Path);
  decorate(tiles, w, h, 16, 16, Tile.Rock);
  decorate(tiles, w, h, 40, 16, Tile.Rock);

  const nodes: ResourceNodeDef[] = [
    { id: "forge-main", kind: "forge", name: "Forge", skillId: "toycraft", x: 28, y: 14 },
    npc("npc-barksmith", "Barksmith Bo", 30, 14, "barksmith"),
    npc("npc-apprentice", "Apprentice Ash", 26, 16, "guide", "Stand near the forge while you smith. Extra pups nearby make it faster."),
  ];

  return mapOf("forgeDen", "Forge Den", w, h, tiles, nodes, [
    { id: "to-hub", x: 24, y: 22, w: 6, h: 5, tag: "Hub", label: "Travel · Hub" },
  ]);
}

export function createVaultMap(): MapData {
  const w = 48;
  const h = 36;
  const tiles = makeGrid(w, h, Tile.Floor);
  treeBorder(tiles, w, h, 2);
  fillRect(tiles, w, h, 15, 22, 21, 25, Tile.Path);
  for (const [x, y] of [
    [8, 8],
    [8, 16],
    [27, 8],
    [27, 16],
    [12, 12],
    [23, 12],
  ] as const) {
    setTile(tiles, w, h, x, y, Tile.Rock);
  }

  const nodes: ResourceNodeDef[] = [
    { id: "bank-chest", kind: "bank", name: "Doggy Bank", x: 18, y: 10 },
    npc("npc-guard", "Guard Grit", 14, 14, "guide", "Pack's full? Click the Doggy Bank."),
  ];

  return mapOf("vault", "Doggy Bank", w, h, tiles, nodes, [
    { id: "to-hub", x: 20, y: 14, w: 6, h: 5, tag: "Hub", label: "Travel · Hub" },
  ]);
}

export function createNeighborhoodMap(): MapData {
  const w = 96;
  const h = 52;
  const tiles = makeGrid(w, h, Tile.Grass);
  sprinkleAlt(tiles, w, h);
  treeBorder(tiles, w, h, 2);
  fillRect(tiles, w, h, 2, 18, 69, 21, Tile.Path);
  for (let i = 0; i < 8; i++) {
    const x = 5 + i * 8;
    fillRect(tiles, w, h, x, 6, x + 3, 11, Tile.Floor);
    fillRect(tiles, w, h, x, 27, x + 3, 32, Tile.Floor);
    paddock(tiles, w, h, x - 1, 5, x + 4, 12, x + 1, 12);
    paddock(tiles, w, h, x - 1, 26, x + 4, 33, x + 1, 26);
    decorate(tiles, w, h, x + 5, 14, Tile.Tree);
    decorate(tiles, w, h, x + 5, 24, Tile.Tree);
  }
  fillRect(tiles, w, h, 2, 18, 69, 21, Tile.Path);

  const nodes: ResourceNodeDef[] = [
    npc("npc-neighbor", "Neighbor Nan", 36, 16, "guide", "Each plot is a kennel. Yours is through the Hub if you still need to unlock it."),
    gatherNode("hood-beg-1", BEG_NODES[1]!, 24, 20),
    gatherNode("hood-beg-2", BEG_NODES[0]!, 52, 20),
  ];

  return mapOf("neighborhood", "Kennel Row", w, h, tiles, nodes, [
    { id: "to-hub", x: 44, y: 16, w: 6, h: 5, tag: "Hub", label: "Travel · Hub" },
  ]);
}

export function createKennelMap(): MapData {
  const w = 48;
  const h = 36;
  const tiles = makeGrid(w, h, Tile.Grass);
  sprinkleAlt(tiles, w, h);
  treeBorder(tiles, w, h, 2);
  fillRect(tiles, w, h, 10, 8, 25, 18, Tile.Floor);
  fillRect(tiles, w, h, 16, 18, 20, 24, Tile.Path);
  paddock(tiles, w, h, 6, 6, 29, 22, 18, 22);
  fillRect(tiles, w, h, 16, 18, 20, 24, Tile.Path);
  fillRect(tiles, w, h, 22, 10, 26, 14, Tile.Flowers);
  pond(tiles, w, h, 9, 16, 2, 2);
  decorate(tiles, w, h, 12, 10, Tile.Bush);
  decorate(tiles, w, h, 24, 16, Tile.Bush);

  const nodes: ResourceNodeDef[] = [
    npc("npc-house", "House Sitter", 18, 12, "guide", "Home sweet kennel. Drop a portal scroll if you want company."),
  ];

  return mapOf("kennel", "Kennel", w, h, tiles, nodes, [
    { id: "to-hub", x: 14, y: 14, w: 6, h: 5, tag: "Hub", label: "Travel · Hub" },
  ]);
}

export function mapForZone(zoneId: ZoneId): MapData {
  switch (zoneId) {
    case "digPark":
    case "fetchWoods":
    case "forgeDen":
    case "vault":
      return createHubMap();
    case "neighborhood":
      return createNeighborhoodMap();
    case "kennel":
      return createKennelMap();
    default:
      return createHubMap();
  }
}

export const SPAWN_BY_ZONE: Record<ZoneId, { x: number; y: number }> = {
  hub: { x: 110.5, y: 96.5 },
  digPark: { x: 74, y: 74 },
  fetchWoods: { x: 158, y: 134 },
  forgeDen: { x: 110, y: 110 },
  vault: { x: 110, y: 110 },
  neighborhood: { x: 48, y: 16 },
  kennel: { x: 18, y: 14 },
};

export const PREVIEW_CAMERA_TILE = { x: 110.5, y: 96.5 } as const;
export const SPAWN_TILE = SPAWN_BY_ZONE.hub;

/** Fraction of the painted spawn radius used for random player landing. */
export const SPAWN_INNER_FRAC = 0.62;

/**
 * Pick a walkable spawn pose. When the map has a spawn marker, land randomly
 * inside an inner circle so pups don't stack on the exact center / rim.
 */
export function pickZoneSpawn(map: MapData, zoneId: ZoneId = map.zoneId): { x: number; y: number } {
  const marker = map.markers?.find((m) => m.kind === "spawn");
  if (marker) {
    const cx = marker.x + marker.w / 2;
    const cy = marker.y + marker.h / 2;
    const outerR = Math.min(marker.w, marker.h) * 0.48;
    const innerR = Math.max(0.75, outerR * SPAWN_INNER_FRAC);
    for (let i = 0; i < 32; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * innerR;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (isWalkablePos(map, x, y)) {
        return { x, y };
      }
    }
    return nearestWalkablePos(map, { x: cx, y: cy }) ?? { x: cx, y: cy };
  }
  const spawn = SPAWN_BY_ZONE[zoneId] ?? SPAWN_BY_ZONE.hub;
  return nearestWalkablePos(map, spawn) ?? spawn;
}
